import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  InputAdornment,
  Tooltip,
  Snackbar,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  Autocomplete,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Drawer,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Save,
  Person,
  Hotel,
  Room as RoomIcon,
  Payment,
  Delete,
  Add,
  Close,
  AccessTime,
  Notes,
  CreditCard,
  Refresh,
  CheckCircle,
  Warning,
  Info,
  CalendarToday,
  NightsStay,
  MeetingRoom,
  ChildCare,
  NightlightRound,
  Remove,
  KeyboardArrowUp,
  KeyboardArrowDown,
  ExpandMore,
  CloudUpload,
  CameraAlt,
  SwapHoriz,
  TrendingUp,
  Percent as PercentIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { HotelDateTimePicker as DateTimePicker } from '../shared/components/forms/HotelDatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import api, { getBookingDetails, updateBooking, getAvailableRoomTypes, getHotelRatePlans, getCheckInAvailableRooms, getAllRooms, getChannels } from '../services/api';
import DashboardHeader from '../components/common/DashboardHeader';
import ChannelImage from '../components/common/ChannelImage';
import EditBookingPageSkeleton from '../components/common/EditBookingPageSkeleton';
import RoomAllocationDrawer from '../components/booking/RoomAllocationDrawer';
import UpgradeRoomDrawer from '../components/booking/UpgradeRoomDrawer';
import EditBookingItemsDrawer from '../components/booking/EditBookingItemsDrawer';
import PaymentDrawerHeaderTitle from '../components/booking/PaymentDrawerHeaderTitle';
import BookingPaymentSummaryCard from '../components/booking/BookingPaymentSummaryCard';
import BookingExtraChargesBreakdown from '../components/booking/BookingExtraChargesBreakdown';
import BookingFinancialHistoryPanel from '../components/booking/BookingFinancialHistoryPanel';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getErrorMessage } from '../utils/errorUtils';
import { formatHotelDate, formatPaymentTime, fromHotelPickerMomentToIso, toHotelPickerMoment, toPaymentProcessedAtIso } from '../utils/dateUtils';
import { PAYMENT_METHOD_CHIPS, paymentMethodToChipLabel } from '../utils/paymentMethodUtils';
import { getBookingRoomAllocationStats } from '../utils/bookingUtils';
import { shouldCheckoutOldRoomSegment, streamHasOpenNightOnOrAfter } from '../utils/roomChangeSegment';
import { getPaymentSummaryData } from '../utils/bookingPaymentSummary';
import {
  resolveActualCheckInDisplay,
  resolveActualCheckOutDisplay,
  resolveActualCheckInMoment,
  resolveActualCheckOutMoment,
  canEditActualCheckIn,
  canEditActualCheckOut,
  resolveStayExclusiveEnd,
  getStayDurationMeta,
} from '../utils/bookingStayDisplay';
import EditableActualStayField from '../components/booking/EditableActualStayField';
import { useGuestSearch } from '../hooks/useGuestSearch';
import { inferBookingTaxInclusive } from '../utils/bookingPriceCalculation';
import { formatDiscountLabel } from '../utils/reviewDiscountUtils';
import {
  getDrawerDatePickerSlotProps,
  getDrawerRootSx,
  getDrawerSelectMenuProps,
} from '../utils/drawerOverlayProps';
import { reviewTaxModeToggleSx, reviewToggleGroupSx } from '../components/steps/reviewStepStyles';
import {
  EDIT_BOOKING_ACCENT,
  getEditBookingTheme,
  getEditBookingPageShellSx,
  getEditBookingHeroPaperSx,
  getEditBookingMainPaperSx,
  getEditBookingSectionCardSx,
  getEditBookingPaymentCardSx,
  getEditBookingFieldSx,
  getEditBookingSelectSx,
  getEditBookingOutlinedChipSx,
  getEditBookingCounterShellSx,
  getEditBookingInfoTooltipButtonSx,
  getEditBookingStatusColor,
} from '../utils/editBookingLayout';

const roundEditBookingAmount = (value) => Math.round(Number(value) * 100) / 100;

function computeEditBookingBreakdown({
  bookingItems,
  discountMode = 'percentage',
  discountPercentage,
  discountAmount,
  gstPercentage,
  gstIncluded,
  taxInclusive,
  booking,
}) {
  const subtotalBeforeDiscount = roundEditBookingAmount(
    bookingItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
  );
  const discountPct = parseFloat(discountPercentage) || 0;
  const discountAmt = roundEditBookingAmount(
    discountMode === 'amount'
      ? Math.min(parseFloat(discountAmount) || 0, subtotalBeforeDiscount)
      : discountPct > 0
        ? subtotalBeforeDiscount * (discountPct / 100)
        : parseFloat(discountAmount) || 0
  );
  const grossAfterDiscount = roundEditBookingAmount(Math.max(0, subtotalBeforeDiscount - discountAmt));
  const gstPct = parseFloat(gstPercentage) || 0;
  const applyInclusive = Boolean(gstIncluded && taxInclusive && gstPct > 0);

  const backendGstAmount = booking?.payment_summary?.gst_amount ?? booking?.gst_amount;
  const backendGstPct =
    parseFloat(booking?.payment_summary?.gst_percentage ?? booking?.gst_percentage ?? 0) || 0;
  const itemGstAmount = bookingItems.reduce((sum, item) => sum + (parseFloat(item.gst_amount) || 0), 0);
  const shouldUseBackendGst = Math.abs(gstPct - backendGstPct) < 0.005;

  let gstAmt = 0;
  let subtotalAfterDiscount = grossAfterDiscount;
  let total = grossAfterDiscount;

  if (gstIncluded && gstPct > 0) {
    if (applyInclusive) {
      gstAmt = roundEditBookingAmount(grossAfterDiscount * gstPct / (100 + gstPct));
      subtotalAfterDiscount = roundEditBookingAmount(grossAfterDiscount - gstAmt);
      total = grossAfterDiscount;
    } else {
      gstAmt = roundEditBookingAmount(
        shouldUseBackendGst && backendGstAmount != null
          ? backendGstAmount
          : itemGstAmount > 0
            ? itemGstAmount
            : grossAfterDiscount * (gstPct / 100)
      );
      total = roundEditBookingAmount(grossAfterDiscount + gstAmt);
    }
  }

  return {
    subtotalBeforeDiscount,
    discountAmt,
    subtotalAfterDiscount,
    grossAfterDiscount,
    gstAmt,
    total,
    taxInclusive: applyInclusive,
    afterDiscountLabel: applyInclusive ? 'Taxable Amount' : 'After discount',
  };
}

function EditBookingDiscountAmountField({ value, onChange, maxAmount, disabled, isDark, fieldSx }) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(value > 0 ? String(roundEditBookingAmount(value)) : '');
    }
  }, [value, focused]);

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseFloat(draft);
    const next = Number.isFinite(parsed)
      ? roundEditBookingAmount(Math.min(Math.max(parsed, 0), maxAmount))
      : 0;
    onChange(next);
    setDraft(next > 0 ? String(next) : '');
  };

  return (
    <TextField
      size="small"
      value={focused ? draft : (value > 0 ? roundEditBookingAmount(value).toFixed(2) : '')}
      onFocus={() => {
        setFocused(true);
        setDraft(value > 0 ? String(roundEditBookingAmount(value)) : '');
      }}
      onChange={(e) => setDraft(e.target.value.replace(/[^\d.]/g, ''))}
      onBlur={handleBlur}
      placeholder="0.00"
      inputMode="decimal"
      disabled={disabled}
      InputProps={{
        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
      }}
      sx={{
        flex: '1 1 140px',
        minWidth: 120,
        maxWidth: 220,
        ...fieldSx,
        '& .MuiOutlinedInput-root': {
          height: 40,
          fontSize: '0.875rem',
        },
      }}
    />
  );
}

// Info icon with tooltip - Material UI creative design (hover icon to see help)
const InfoTooltip = ({ title, isDark, size = 16 }) => (
  <Tooltip
    title={title}
    arrow
    placement="top"
    slotProps={{
      popper: { sx: { '& .MuiTooltip-tooltip': { maxWidth: 280, fontSize: '0.8rem', fontWeight: 400, px: 1.5, py: 1.25 } } },
    }}
  >
    <IconButton
      size="small"
      disableRipple
      sx={getEditBookingInfoTooltipButtonSx(isDark, size)}
      aria-label="More info"
    >
      <Info sx={{ fontSize: size }} />
    </IconButton>
  </Tooltip>
);

// Booking source options (match backend Booking.BookingSource)
const BOOKING_SOURCE_OPTIONS = [
  { value: 'direct', label: 'Direct (Roomnexa)' },
  { value: 'booking_engine', label: 'Booking Engine (NexaStay)' },
  { value: 'ota', label: 'OTA (Online Travel Agency)' },
  { value: 'walk_in', label: 'Walk-In' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'group', label: 'Group Booking' },
  { value: 'agent', label: 'Travel Agent' },
  { value: 'api', label: 'API Integration' },
  { value: 'other', label: 'Other' },
];

const EditBookingPage = () => {
  const { bookingId } = useParams();
  const normalizedBookingId = useMemo(() => {
    const parsedId = Number(bookingId);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  }, [bookingId]);
  const theme = useTheme();
  
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const surface = useMemo(() => getEditBookingTheme(isDark), [isDark]);
  const drawerSelectMenuProps = useMemo(
    () => getDrawerSelectMenuProps(isDark, surface.surface),
    [isDark, surface.surface],
  );
  const drawerDatePickerSlotProps = useMemo(
    () => getDrawerDatePickerSlotProps(isDark, surface.surface),
    [isDark, surface.surface],
  );

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [cameraPermissionMessage, setCameraPermissionMessage] = useState(null);
  const [booking, setBooking] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [availableRooms, setAvailableRooms] = useState({}); // { room_type_id: [{ id, room_number }] }
  const [addRoomTypeGroups, setAddRoomTypeGroups] = useState([]); // full room-type groups from availability API
  const [availabilityByRange, setAvailabilityByRange] = useState({}); // { "start_end": { room_types } } for room type dropdown filtering
  const [addPaymentDrawerOpen, setAddPaymentDrawerOpen] = useState(false);
  const [newPaymentAmount, setNewPaymentAmount] = useState('');
  const [newPaymentDate, setNewPaymentDate] = useState(() => moment());
  const [newPaymentMethod, setNewPaymentMethod] = useState('CASH');
  const [newPaymentReference, setNewPaymentReference] = useState('');
  const [addingPayment, setAddingPayment] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [paymentCameraOpen, setPaymentCameraOpen] = useState(false);
  const [paymentStream, setPaymentStream] = useState(null);
  const [paymentCameraUpload, setPaymentCameraUpload] = useState(false);
  const addPaymentDrawerRootRef = useRef(null);
  const paymentMethods = PAYMENT_METHOD_CHIPS;
  const paymentReceiptVideoRef = useRef(null);
  const paymentReceiptCanvasRef = useRef(null);
  const paymentReceiptFileInputRef = useRef(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, payment: null });
  const [removeRoomConfirmDialog, setRemoveRoomConfirmDialog] = useState({
    open: false,
    group: null,
    totalDisplayRows: 0,
    targetItemIds: [],
  });
  const [removeRoomConfirmText, setRemoveRoomConfirmText] = useState('');
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false); // Modal popup for save/action errors
  const [resetBookingDatesOnErrorClose, setResetBookingDatesOnErrorClose] = useState(false);
  const [saveConfirmDialog, setSaveConfirmDialog] = useState({ open: false, section: null });
  const [savingSection, setSavingSection] = useState(null); // 'booking' | 'rooms' | 'guest' | 'payment'
  const [validationErrors, setValidationErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    booking: true,
    rooms: true,
    guest: true,
    payment: true,
  });
  const [allocationDrawerOpen, setAllocationDrawerOpen] = useState(false);
  const [changeRoomDrawerOpen, setChangeRoomDrawerOpen] = useState(false);
  const [changeRoomDrawerGroup, setChangeRoomDrawerGroup] = useState(null);
  const [upgradeDrawerOpen, setUpgradeDrawerOpen] = useState(false);
  const [upgradeDrawerGroup, setUpgradeDrawerGroup] = useState(null);
  const [editItemsDrawerOpen, setEditItemsDrawerOpen] = useState(false);
  const [editItemsDrawerGroup, setEditItemsDrawerGroup] = useState(null);
  const [addRoomDrawerOpen, setAddRoomDrawerOpen] = useState(false);
  const [addRoomFilterTypeId, setAddRoomFilterTypeId] = useState('all');
  const [addRoomSelectedTypeId, setAddRoomSelectedTypeId] = useState('');
  const [addRoomSelectedRoomId, setAddRoomSelectedRoomId] = useState(null);
  const [addRoomAdults, setAddRoomAdults] = useState(1);
  const [addRoomChildren, setAddRoomChildren] = useState(0);
  const [addRoomRatePerNight, setAddRoomRatePerNight] = useState(0);
  const [addRoomSubmitting, setAddRoomSubmitting] = useState(false);
  const [addRoomError, setAddRoomError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    check_in_date: null,
    check_out_date: null,
    booking_date_time: null,
    expected_arrival_time: null,
    pax_adults: 1,
    pax_children: 0,
    total_rooms: 1,
    total_amount: 0,
    advance_amount: 0,
    special_requests: '',
    internal_notes: '',
    staff_notes: '',
    discount_percentage: 0,
    discount_amount: 0,
    discount_mode: 'percentage',
    gst_percentage: 0,
    gst_included: false,
    tax_inclusive: false,
    is_paid: false,
    channel_id: null,
    channel_booking_code: '',
    provider_booking_id: '',
    booking_source: '',
    status: '',
  });
  const [channels, setChannels] = useState([]);

  // Assign guest: single primary guest for the booking (one guest only; room/item guests can be multiple)
  const [selectedGuests, setSelectedGuests] = useState([]);
  // Booking guest dropdown: separate state so it doesn't conflict with room-details guest dropdown
  const [bookingGuestSearchInput, setBookingGuestSearchInput] = useState('');
  // Room details guest dropdown(s): separate state so it doesn't conflict with booking guest dropdown
  const [roomGuestSearchInput, setRoomGuestSearchInput] = useState('');

  // Booking items state
  const [bookingItems, setBookingItems] = useState([]);


  // Load booking data (parallel fetch; AbortController for unmount/cancel)
  const fetchBookingData = useCallback(async (signal) => {
    if (!normalizedBookingId) {
      setError('Invalid booking ID');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const apiConfig = signal ? { signal } : {};
      const [bookingData, ratePlansData, availableRoomsData, channelsData] = await Promise.all([
        getBookingDetails(normalizedBookingId, apiConfig),
        getHotelRatePlans(apiConfig).catch(() => []),
        getCheckInAvailableRooms(normalizedBookingId, apiConfig).catch(() => ({ room_types: [] })),
        getChannels(apiConfig).catch(() => ({ channels: [] })),
      ]);

      if (signal?.aborted) return;
      setChannels(channelsData?.channels ?? []);

      setBooking(bookingData);

      const checkIn = bookingData.check_in_date ? moment(bookingData.check_in_date) : null;
      const checkOut = bookingData.check_out_date ? moment(bookingData.check_out_date) : null;
      const excludeBookingId = bookingData?.id ? parseInt(bookingData.id, 10) : null;

      // Fetch room types for full range (baseline list) and for each distinct "affected range" per row
      const bookingItemsData = bookingData.items || bookingData.booking_items || [];
      const distinctStartDates = [...new Set(
        bookingItemsData
          .filter((i) => i.date)
          .map((i) => moment(i.date).format('YYYY-MM-DD'))
          .sort()
      )];
      const rangeKeys = distinctStartDates.map((start) => {
        const end = checkOut ? checkOut.format('YYYY-MM-DD') : moment(start).add(1, 'day').format('YYYY-MM-DD');
        return `${start}_${end}`;
      });

      const availConfig = { ...apiConfig, exclude_booking_id: excludeBookingId || undefined };
      const roomTypesPromises = [
        checkIn && checkOut
          ? getAvailableRoomTypes(checkIn.format('YYYY-MM-DD'), checkOut.format('YYYY-MM-DD'), availConfig)
              .catch(() => ({ room_types: [] }))
          : Promise.resolve({ room_types: [] }),
        ...rangeKeys.map((key) => {
          const [start, end] = key.split('_');
          return getAvailableRoomTypes(start, end, availConfig)
            .then((data) => ({ key, data }))
            .catch(() => ({ key, data: { room_types: [] } }));
        }),
      ];

      const walkinStyleAllRoomsPromise =
        checkIn && checkOut
          ? getAllRooms({
              check_in_date: checkIn.format('YYYY-MM-DD'),
              check_out_date: checkOut.format('YYYY-MM-DD'),
            }).catch(() => ({ room_types: [] }))
          : Promise.resolve({ room_types: [] });

      const [roomTypesResults, walkinStyleAllRoomsData] = await Promise.all([
        Promise.all(roomTypesPromises),
        walkinStyleAllRoomsPromise,
      ]);
      if (signal?.aborted) return;

      const baseRoomTypes = roomTypesResults[0]?.room_types || [];
      setRoomTypes(baseRoomTypes);

      const byRange = {};
      roomTypesResults.slice(1).forEach(({ key, data }) => {
        byRange[key] = data;
      });
      setAvailabilityByRange(byRange);
      setRatePlans(ratePlansData?.rate_plans || ratePlansData || []);
      // Build available rooms map: { room_type_id: [{ id, room_number }] }
      const roomsMap = {};
      const roomTypesList = availableRoomsData?.room_types ?? (Array.isArray(availableRoomsData) ? availableRoomsData : []);
      (Array.isArray(roomTypesList) ? roomTypesList : []).forEach(rt => {
        const rtId = rt.room_type?.id ?? rt.id;
        const rooms = rt.rooms || [];
        if (rtId) {
          roomsMap[rtId] = rooms.map(r => ({
            id: r.id,
            room_number: r.room_number ?? r.number ?? r.name,
            is_available: r.is_available,
          }));
        }
      });
      setAvailableRooms(roomsMap);
      const walkinRoomTypeList = walkinStyleAllRoomsData?.room_types ?? [];
      setAddRoomTypeGroups(
        Array.isArray(walkinRoomTypeList) && walkinRoomTypeList.length > 0
          ? walkinRoomTypeList
          : (Array.isArray(roomTypesList) ? roomTypesList : [])
      );

      const initItems = bookingData.items || bookingData.booking_items || [];
      const activePaxByStream = new Map();
      initItems.forEach((item) => {
        if (item?.checkout === true) return;
        const typeId = Number(item?.room_type?.id ?? item?.room_type_id ?? 0) || 'unknown';
        const seq = Number(item?.room_sequence || 1) || 1;
        const streamKey = `${typeId}_${seq}`;
        if (!activePaxByStream.has(streamKey)) {
          activePaxByStream.set(streamKey, {
            adults: Math.max(0, Number(item?.adults ?? 0)),
            children: Math.max(0, Number(item?.children ?? 0)),
          });
        }
      });
      const derivedPax = Array.from(activePaxByStream.values()).reduce(
        (acc, stream) => ({
          adults: acc.adults + Math.max(0, Number(stream?.adults ?? 0)),
          children: acc.children + Math.max(0, Number(stream?.children ?? 0)),
        }),
        { adults: 0, children: 0 }
      );
      const derivedTotalRooms = activePaxByStream.size;
      const resolvedPaxAdults =
        derivedTotalRooms > 0
          ? Math.max(1, derivedPax.adults)
          : Math.max(1, Number(bookingData.pax_adults ?? 1) || 1);
      const resolvedPaxChildren =
        derivedTotalRooms > 0
          ? Math.max(0, derivedPax.children)
          : Math.max(0, Number(bookingData.pax_children ?? 0) || 0);
      const resolvedTotalRooms =
        derivedTotalRooms > 0
          ? Math.max(1, derivedTotalRooms)
          : Math.max(1, Number(bookingData.total_rooms ?? 1) || 1);

      // Initialize form data
      setFormData({
        check_in_date: bookingData.check_in_date ? moment(bookingData.check_in_date) : null,
        check_out_date: bookingData.check_out_date ? moment(bookingData.check_out_date) : null,
        booking_date_time: bookingData.created_at
          ? toHotelPickerMoment(bookingData.created_at, bookingData.hotel_timezone)
          : null,
        expected_arrival_time: bookingData.expected_arrival_time 
          ? moment(bookingData.expected_arrival_time, 'HH:mm:ss') 
          : null,
        pax_adults: resolvedPaxAdults,
        pax_children: resolvedPaxChildren,
        total_rooms: resolvedTotalRooms,
        total_amount: bookingData.total_amount || 0,
        advance_amount: bookingData.advance_amount || 0,
        special_requests: bookingData.special_requests || '',
        internal_notes: bookingData.internal_notes || '',
        staff_notes: bookingData.staff_notes || '',
        discount_percentage: bookingData.discount_percentage || 0,
        discount_amount: bookingData.discount_amount || 0,
        discount_mode:
          (bookingData.discount_amount || 0) > 0 && !(bookingData.discount_percentage || 0)
            ? 'amount'
            : 'percentage',
        gst_percentage: bookingData.gst_percentage || 0,
        gst_included: bookingData.gst_included || false,
        tax_inclusive: inferBookingTaxInclusive({
          gstIncluded: bookingData.gst_included || false,
          taxInclusive: bookingData.tax_inclusive ?? bookingData.payment_summary?.tax_inclusive ?? false,
          subtotalBeforeDiscount: bookingData.subtotal_before_discount ?? bookingData.payment_summary?.subtotal_before_discount ?? 0,
          discountAmount: bookingData.discount_amount ?? bookingData.payment_summary?.discount_amount ?? 0,
          totalAmount: bookingData.total_amount ?? bookingData.payment_summary?.total_amount ?? 0,
        }),
        is_paid: bookingData.is_paid || false,
        channel_id: bookingData.channel?.id ?? null,
        channel_booking_code: bookingData.channel_booking_code || '',
        provider_booking_id: bookingData.provider_booking_id || '',
        booking_source: bookingData.booking_source || '',
        status: bookingData.status || '',
      });

      // Initialize selected guest (primary guest from booking; one guest only)
      const guest = bookingData.guest;
      if (guest && guest.id) {
        const fullName = [guest.first_name, guest.middle_name, guest.last_name].filter(Boolean).join(' ').trim() || guest.name || 'Guest';
        setSelectedGuests([{
          id: guest.id,
          name: fullName,
          email: guest.email || '—',
          phone: guest.phone || '—',
          country_code: guest.country_code || '+91',
        }]);
      } else {
        setSelectedGuests([]);
      }

      // Initialize booking items (preserve room_type so image_url is available in Daily Rate drawer)
      setBookingItems(initItems.map(item => ({
        id: item.id,
        room_type_id: item.room_type?.id,
        room_type_name: item.room_type?.name,
        room_type: item.room_type ? { id: item.room_type.id, name: item.room_type.name, image_url: item.room_type.image_url, image: item.room_type.image } : undefined,
        rate_plan_id: item.rate_plan?.id,
        rate_plan_name: item.rate_plan?.name,
        allocated_room_id: item.allocated_room?.id,
        allocated_room_number: item.allocated_room?.room_number || item.allocated_room?.number,
        price: item.price || 0,
        gst_amount: item.gst_amount || 0,
        gst_percentage: item.gst_percentage || 0,
        total: item.total || 0,
        date: item.date,
        adults: item.adults ?? 1,
        children: item.children ?? 0,
        room_sequence: item.room_sequence || 1,
        guests: item.guests || [],
        checked_in: item.checked_in ?? false,
        checkout: item.checkout ?? false,
        upgraded_from_room_type_id: item.upgraded_from_room_type_id ?? item.upgraded_from_room_type?.id ?? null,
      })));

      return bookingData;
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') return;
      console.error('Error fetching booking:', err);
      setError(getErrorMessage(err, 'Failed to load booking data'));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [normalizedBookingId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchBookingData(controller.signal);
    return () => controller.abort();
  }, [fetchBookingData]);

  // Handle section expansion
  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Reusable guest search (debounced, abortable)
  const { options: bookingGuestOptions, loading: bookingGuestOptionsLoading } = useGuestSearch({
    searchInput: bookingGuestSearchInput,
    enabled: !!booking?.id,
  });
  const { options: roomGuestOptions, loading: roomGuestOptionsLoading } = useGuestSearch({
    searchInput: roomGuestSearchInput,
    enabled: !!booking?.id,
  });

  // Handle form changes - when total_amount changes, sync bookingItems proportionally
  const handleFormChange = (field, value) => {
    if (field === 'total_amount') {
      const targetTotal = parseFloat(value) || 0;
      if (targetTotal > 0 && bookingItems.length > 0) {
        const currentSubtotal = bookingItems.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);
        const discountAmt =
          formData.discount_mode === 'amount'
            ? Math.min(parseFloat(formData.discount_amount) || 0, currentSubtotal)
            : formData.discount_percentage > 0
              ? currentSubtotal * (formData.discount_percentage / 100)
              : parseFloat(formData.discount_amount) || 0;
        const round2 = (x) => Math.round(Number(x) * 100) / 100;
        const afterDiscount = round2(Math.max(0, currentSubtotal - discountAmt));
        const applyInclusive = formData.gst_included && formData.tax_inclusive && formData.gst_percentage > 0;
        let gstAmt = 0;
        let currentTotal = afterDiscount;
        if (formData.gst_included && formData.gst_percentage > 0) {
          if (applyInclusive) {
            gstAmt = round2(afterDiscount * formData.gst_percentage / (100 + formData.gst_percentage));
          } else {
            gstAmt = round2(afterDiscount * (formData.gst_percentage / 100));
            currentTotal = round2(afterDiscount + gstAmt);
          }
        }
        if (currentTotal > 0) {
          const scale = targetTotal / currentTotal;
          setBookingItems(prev => prev.map(item => ({
            ...item,
            price: parseFloat(((parseFloat(item.price) || 0) * scale).toFixed(2)),
          })));
          const newSubtotal = round2(currentSubtotal * scale);
          const newDiscount = round2(
            formData.discount_mode === 'amount'
              ? Math.min((parseFloat(formData.discount_amount) || 0) * scale, newSubtotal)
              : formData.discount_percentage > 0
                ? newSubtotal * (formData.discount_percentage / 100)
                : (parseFloat(formData.discount_amount) || 0) * scale
          );
          setFormData(prev => ({
            ...prev,
            total_amount: round2(parseFloat(value) || 0),
            discount_amount: newDiscount,
            discount_percentage: prev.discount_percentage,
            discount_mode: prev.discount_mode,
            gst_percentage: prev.gst_percentage,
            gst_included: prev.gst_included,
            tax_inclusive: prev.tax_inclusive,
          }));
          return;
        }
      }
    }
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'gst_included' && !value ? { tax_inclusive: false } : {}),
    }));
  };

  const resetFormDatesToPersistedBooking = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      check_in_date: booking?.check_in_date ? moment(booking.check_in_date) : null,
      check_out_date: booking?.check_out_date ? moment(booking.check_out_date) : null,
    }));
    setValidationErrors((prev) => ({
      ...prev,
      check_in_date: undefined,
      check_out_date: undefined,
    }));
  }, [booking?.check_in_date, booking?.check_out_date]);

  const closeErrorDialog = useCallback(() => {
    setSaveConfirmDialog({ open: false, section: null });
    setErrorDialogOpen(false);
    setError(null);
    if (resetBookingDatesOnErrorClose) {
      resetFormDatesToPersistedBooking();
      setResetBookingDatesOnErrorClose(false);
    }
  }, [resetBookingDatesOnErrorClose, resetFormDatesToPersistedBooking]);

  const handleBookingItemChange = (index, field, value) => {
    setBookingItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  // When room type changes via dropdown:
  // - If first date in sequence: update all dates in that sequence to new room type
  // - If mid-date (e.g. 8th when we have 7,8,9,10): update 8th onward to new room type, mark prior (7th) checkout
  const handleRoomTypeChange = (index, newRoomTypeId, groupContext) => {
    const item = bookingItems[index];
    if (!item || newRoomTypeId === item.room_type_id) return;
    const rt = roomTypes.find(r => r.id === newRoomTypeId);
    if (!newRoomTypeId) {
      handleBookingItemChange(index, 'room_type_id', null);
      handleBookingItemChange(index, 'room_type_name', '');
      handleBookingItemChange(index, 'allocated_room_id', null);
      return;
    }

    const maxSeq = Math.max(0, ...bookingItems.map(i => i.room_sequence ?? 1));
    const newSeq = maxSeq + 1;
    const priorIndices = groupContext?.priorIndices ?? [];
    const updateIndices = groupContext?.updateIndices ?? [index];

    setBookingItems(prev => {
      const updated = [...prev];
      priorIndices.forEach((i) => {
        updated[i] = { ...updated[i], checkout: true };
      });
      updateIndices.forEach((i) => {
        updated[i] = {
          ...updated[i],
          room_type_id: newRoomTypeId,
          room_type_name: rt?.name || '',
          room_sequence: newSeq,
          allocated_room_id: null,
        };
      });
      return updated;
    });
  };

  // Update all items in a room group (propagate price, allocated_room, rate_plan across all days)
  const handleBookingGroupChange = (groupItems, field, value) => {
    setBookingItems(prev => {
      const updated = [...prev];
      groupItems.forEach(({ index }) => {
        updated[index] = { ...updated[index], [field]: value };
      });
      return updated;
    });
  };

  const buildRoomsUpdatePayload = useCallback((items, extraData = {}) => ({
    ...extraData,
    booking_items: items.map((item) => ({
      id: item.id,
      date: item.date,
      price: parseFloat(item.price) || 0,
      rate_plan_id: item.rate_plan_id || null,
      allocated_room_id: item.allocated_room_id || null,
      room_type_id: item.room_type_id || null,
      room_sequence: item.room_sequence ?? 1,
      checked_in: item.checked_in ?? false,
      checkout: item.checkout ?? false,
      upgraded_from_room_type_id: item.upgraded_from_room_type_id ?? null,
      adults: item.adults,
      children: item.children,
      ...(item.segment_started_at ? { segment_started_at: item.segment_started_at } : {}),
      ...(item.segment_ended_at ? { segment_ended_at: item.segment_ended_at } : {}),
      ...(item.room_changed_at ? { room_changed_at: item.room_changed_at } : {}),
    })),
    // Room guest assignments are managed via check-in drawer APIs — do not sync here
    // or id-only payloads can wipe guest profile fields on unrelated room saves.
  }), []);

  const persistRoomDetailsChanges = useCallback(async (nextItems, { successMessage = null, nextTotalRooms = null } = {}) => {
    try {
      setSavingSection('rooms');
      setBookingItems(nextItems);
      const activePaxByStream = new Map();
      nextItems.forEach((item) => {
        if (item?.checkout === true) return;
        const typeKey = Number(item?.room_type_id || 0) || 'unknown';
        const seqKey = Number(item?.room_sequence || 1) || 1;
        // Keep pax in sync with unique booking streams (room_type + room_sequence),
        // not per allocated room/date, so upgrade/change-room does not overcount.
        const streamKey = `${typeKey}_${seqKey}`;
        if (!activePaxByStream.has(streamKey)) {
          activePaxByStream.set(streamKey, {
            adults: Math.max(0, Number(item?.adults || 0)),
            children: Math.max(0, Number(item?.children || 0)),
          });
        }
      });
      const paxTotals = Array.from(activePaxByStream.values()).reduce(
        (acc, stream) => ({
          adults: acc.adults + Math.max(0, Number(stream?.adults || 0)),
          children: acc.children + Math.max(0, Number(stream?.children || 0)),
        }),
        { adults: 0, children: 0 }
      );
      const derivedTotalRooms = Math.max(1, activePaxByStream.size || 0);
      const resolvedTotalRooms = nextTotalRooms != null ? nextTotalRooms : derivedTotalRooms;
      if (nextTotalRooms != null) {
        setFormData((prev) => ({
          ...prev,
          total_rooms: resolvedTotalRooms,
          pax_adults: paxTotals.adults,
          pax_children: paxTotals.children,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          total_rooms: resolvedTotalRooms,
          pax_adults: paxTotals.adults,
          pax_children: paxTotals.children,
        }));
      }
      const extraData = {
        total_rooms: resolvedTotalRooms,
        pax_adults: paxTotals.adults,
        pax_children: paxTotals.children,
      };
      await updateBooking(bookingId, buildRoomsUpdatePayload(nextItems, extraData));
      if (successMessage) setSuccessMessage(successMessage);
      await fetchBookingData();
    } catch (err) {
      const errMsg = getErrorMessage(err, 'Failed to update room details.');
      setError(errMsg);
      setErrorDialogOpen(true);
    } finally {
      setSavingSection(null);
    }
  }, [bookingId, buildRoomsUpdatePayload, fetchBookingData]);

  const addRoomTypeOptions = useMemo(() => {
    return (addRoomTypeGroups || [])
      .map((group) => {
        const typeId = group?.room_type?.id ?? group?.id ?? null;
        const typeName = group?.room_type?.name ?? group?.name ?? 'Room Type';
        const availableCount = (group?.rooms || []).filter((r) => r?.is_available !== false).length;
        return {
          id: typeId ? String(typeId) : '',
          name: typeName,
          availableCount,
          rooms: group?.rooms || [],
          image: group?.room_type?.image ?? group?.image ?? null,
          image_url: group?.room_type?.image_url ?? group?.image_url ?? null,
          base_adults: Number(group?.room_type?.base_adults ?? group?.base_adults ?? 1) || 1,
          base_children: Number(group?.room_type?.base_children ?? group?.base_children ?? 0) || 0,
          max_guests: Number(group?.room_type?.max_guests ?? group?.max_guests ?? 4) || 4,
        };
      })
      .filter((opt) => opt.id && opt.availableCount > 0)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [addRoomTypeGroups]);

  const addRoomDisplayGroups = useMemo(() => {
    const base = addRoomTypeOptions.map((opt) => ({
      id: opt.id,
      name: opt.name,
      rooms: (opt.rooms || []).filter((r) => r?.is_available !== false),
      availableCount: opt.availableCount,
      image: opt.image,
      image_url: opt.image_url,
      base_adults: opt.base_adults,
      base_children: opt.base_children,
      max_guests: opt.max_guests,
    }));
    if (addRoomFilterTypeId === 'all') return base;
    return base.filter((group) => String(group.id) === String(addRoomFilterTypeId));
  }, [addRoomTypeOptions, addRoomFilterTypeId]);

  const selectedAddRoomType = useMemo(
    () => addRoomTypeOptions.find((opt) => String(opt.id) === String(addRoomSelectedTypeId)) || null,
    [addRoomTypeOptions, addRoomSelectedTypeId]
  );
  const getAddRoomPricingConfig = useCallback((roomTypeId) => {
    const typeIdNum = Number(roomTypeId || 0);
    if (!typeIdNum) return null;
    const source = roomTypes.find((rt) => Number(rt?.id || 0) === typeIdNum);
    const typeMeta = addRoomTypeOptions.find((opt) => Number(opt?.id || 0) === typeIdNum);
    const parseRate = (value, fallback = 0) => {
      const parsed = parseFloat(value);
      if (Number.isFinite(parsed) && parsed >= 0) return parsed;
      const fallbackParsed = parseFloat(fallback);
      return Number.isFinite(fallbackParsed) && fallbackParsed >= 0 ? fallbackParsed : 0;
    };
    const baseRate = parseRate(source?.base_rate ?? source?.final_rate ?? source?.price, 0);
    const singleRate = parseRate(source?.single_occupancy_rate, baseRate);
    const extraAdultRate = parseRate(source?.extra_adult_rate, 0);
    const extraChildRate = parseRate(source?.extra_child_rate, 0);
    const baseAdults = Math.max(1, Number(source?.base_adults ?? typeMeta?.base_adults ?? 1) || 1);
    const baseChildren = Math.max(0, Number(source?.base_children ?? typeMeta?.base_children ?? 0) || 0);
    return {
      baseRate,
      singleRate,
      extraAdultRate,
      extraChildRate,
      baseAdults,
      baseChildren,
    };
  }, [roomTypes, addRoomTypeOptions]);
  const computeAddRoomRatePerNight = useCallback((roomTypeId, adultsCount, childrenCount, fallback = 0) => {
    const pricing = getAddRoomPricingConfig(roomTypeId);
    if (!pricing) {
      const fallbackParsed = parseFloat(fallback);
      return Number.isFinite(fallbackParsed) && fallbackParsed >= 0 ? fallbackParsed : 0;
    }
    const adults = Math.max(0, Number(adultsCount || 0));
    const children = Math.max(0, Number(childrenCount || 0));
    const extraAdults = Math.max(0, adults - pricing.baseAdults);
    const extraChildren = Math.max(0, children - pricing.baseChildren);
    const baseNightRate = adults <= 1 ? pricing.singleRate : pricing.baseRate;
    const total = baseNightRate + (extraAdults * pricing.extraAdultRate) + (extraChildren * pricing.extraChildRate);
    return Math.max(0, Math.round(total * 100) / 100);
  }, [getAddRoomPricingConfig]);
  const addRoomMaxGuests = Number(selectedAddRoomType?.max_guests || 4) || 4;
  const addRoomMaxAdults = Math.max(0, addRoomMaxGuests - Number(addRoomChildren || 0));
  const addRoomMaxChildren = Math.max(0, addRoomMaxGuests - Number(addRoomAdults || 0));
  const canAddRoomBeforeOrOnCheckIn = useMemo(() => {
    const checkInSource = formData.check_in_date || booking?.check_in_date;
    if (!checkInSource) return false;
    const checkInDate = moment.isMoment(checkInSource)
      ? checkInSource.clone().startOf('day')
      : moment(checkInSource).startOf('day');
    if (!checkInDate.isValid()) return false;
    return moment().startOf('day').isSameOrBefore(checkInDate);
  }, [formData.check_in_date, booking?.check_in_date]);
  const addRoomDateRuleMessage = 'Add Room is allowed only before check-in date or on check-in date.';

  const handleOpenAddRoomDrawer = useCallback(() => {
    if (!canAddRoomBeforeOrOnCheckIn) {
      setError(addRoomDateRuleMessage);
      setErrorDialogOpen(true);
      return;
    }
    const templateItem = bookingItems.find((it) => it?.checkout !== true) || bookingItems[0];
    const defaultAdults = Math.max(0, Number(templateItem?.adults ?? 1) || 1);
    const defaultChildren = Math.max(0, Number(templateItem?.children ?? 0) || 0);
    const defaultPrice = Math.max(0, parseFloat(templateItem?.price) || 0);
    setAddRoomError('');
    setAddRoomFilterTypeId('all');
    setAddRoomSelectedTypeId('');
    setAddRoomSelectedRoomId(null);
    setAddRoomAdults(defaultAdults);
    setAddRoomChildren(defaultChildren);
    setAddRoomRatePerNight(defaultPrice);
    setAddRoomDrawerOpen(true);
  }, [bookingItems, canAddRoomBeforeOrOnCheckIn, addRoomDateRuleMessage]);

  const handleAddRoomGuestCountChange = useCallback((field, delta) => {
    if (field === 'adults') {
      setAddRoomAdults((prevAdults) => {
        const nextAdults = Math.max(0, Math.min(addRoomMaxAdults, (Number(prevAdults) || 0) + delta));
        setAddRoomChildren((prevChildren) => {
          const maxChildrenForAdults = Math.max(0, addRoomMaxGuests - nextAdults);
          return Math.max(0, Math.min(Number(prevChildren) || 0, maxChildrenForAdults));
        });
        return nextAdults;
      });
      return;
    }

    setAddRoomChildren((prevChildren) => {
      const nextChildren = Math.max(0, Math.min(addRoomMaxChildren, (Number(prevChildren) || 0) + delta));
      setAddRoomAdults((prevAdults) => {
        const maxAdultsForChildren = Math.max(0, addRoomMaxGuests - nextChildren);
        return Math.max(0, Math.min(Number(prevAdults) || 0, maxAdultsForChildren));
      });
      return nextChildren;
    });
  }, [addRoomMaxAdults, addRoomMaxChildren, addRoomMaxGuests]);

  useEffect(() => {
    if (!addRoomSelectedTypeId) return;
    const nextRate = computeAddRoomRatePerNight(
      addRoomSelectedTypeId,
      addRoomAdults,
      addRoomChildren,
      addRoomRatePerNight
    );
    setAddRoomRatePerNight((prevRate) => {
      const prevNum = Number(prevRate || 0);
      return Math.abs(prevNum - nextRate) > 0.009 ? nextRate : prevNum;
    });
  }, [addRoomSelectedTypeId, addRoomAdults, addRoomChildren, computeAddRoomRatePerNight]);

  const handleConfirmAddRoom = useCallback(async () => {
    if (!isEditable) return;
    if (!canAddRoomBeforeOrOnCheckIn) {
      setAddRoomError(addRoomDateRuleMessage);
      return;
    }
    const selectedTypeIdNum = Number(addRoomSelectedTypeId || 0);
    const selectedRoomIdNum = Number(addRoomSelectedRoomId || 0);
    if (!selectedTypeIdNum || !selectedRoomIdNum) {
      setAddRoomError('Please select a room type and room.');
      return;
    }
    if (Number(addRoomAdults || 0) + Number(addRoomChildren || 0) <= 0) {
      setAddRoomError('Please add at least one guest (adult or child).');
      return;
    }
    if ((Number(addRoomAdults || 0) + Number(addRoomChildren || 0)) > addRoomMaxGuests) {
      setAddRoomError(`Total guests cannot exceed ${addRoomMaxGuests} for this room type.`);
      return;
    }

    const checkInMoment = formData.check_in_date?.isValid?.()
      ? formData.check_in_date.clone().startOf('day')
      : (booking?.check_in_date ? moment(booking.check_in_date).startOf('day') : null);
    const checkOutMoment = formData.check_out_date?.isValid?.()
      ? formData.check_out_date.clone().startOf('day')
      : (booking?.check_out_date ? moment(booking.check_out_date).startOf('day') : null);
    if (
      !checkInMoment
      || !checkOutMoment
      || !checkInMoment.isValid()
      || !checkOutMoment.isValid()
      || checkOutMoment.isBefore(checkInMoment, 'day')
    ) {
      setAddRoomError('Invalid check-in/check-out dates.');
      return;
    }
    // Same-day (hourly / day-use) stays hold one room-night on the check-in date.
    const stayEndMoment = resolveStayExclusiveEnd(checkInMoment, checkOutMoment);

    // Do not allow selecting the same room for multiple active rows on the same date.
    let cursorForConflict = checkInMoment.clone();
    while (cursorForConflict.isBefore(stayEndMoment, 'day')) {
      const dateStr = cursorForConflict.format('YYYY-MM-DD');
      const conflict = bookingItems.some((it) => {
        const itemDate = it?.date
          ? (moment.isMoment(it.date) ? it.date.format('YYYY-MM-DD') : String(it.date).slice(0, 10))
          : '';
        return itemDate === dateStr && it?.checkout !== true && Number(it?.allocated_room_id || 0) === selectedRoomIdNum;
      });
      if (conflict) {
        setAddRoomError(`Room already allocated for ${dateStr}. Please choose another room.`);
        return;
      }
      cursorForConflict.add(1, 'day');
    }

    const selectedType = roomTypes.find((rt) => Number(rt?.id || 0) === selectedTypeIdNum);
    const selectedGroup = addRoomDisplayGroups.find((g) => Number(g?.id || 0) === selectedTypeIdNum);
    const roomFromGroup = selectedGroup?.rooms?.find((r) => Number(r?.id || 0) === selectedRoomIdNum);
    // Prefer an existing active item as a template; if the booking has no items left
    // (e.g. same-day stay wipe), fall back to selected type / rate plan defaults.
    const templateItem =
      bookingItems.find((it) => Number(it?.room_type_id || 0) === selectedTypeIdNum && it?.checkout !== true) ||
      bookingItems.find((it) => it?.checkout !== true) ||
      bookingItems[0] ||
      null;
    const defaultRatePlan =
      ratePlans.find((rp) => Number(rp?.id || 0) === Number(templateItem?.rate_plan_id || 0)) ||
      ratePlans.find((rp) => rp?.is_active !== false) ||
      ratePlans[0] ||
      null;

    const existingSequences = bookingItems
      .filter((it) => Number(it?.room_type_id || 0) === selectedTypeIdNum)
      .map((it) => Number(it?.room_sequence || 1));
    const nextSequence = (existingSequences.length ? Math.max(0, ...existingSequences) : 0) + 1;

    const newRowItems = [];
    const cursor = checkInMoment.clone();
    while (cursor.isBefore(stayEndMoment, 'day')) {
      newRowItems.push({
        id: null,
        room_type_id: selectedTypeIdNum,
        room_type_name:
          selectedType?.name ||
          selectedGroup?.name ||
          templateItem?.room_type_name ||
          '',
        rate_plan_id: templateItem?.rate_plan_id || defaultRatePlan?.id || null,
        rate_plan_name: templateItem?.rate_plan_name || defaultRatePlan?.name || defaultRatePlan?.code || '',
        allocated_room_id: selectedRoomIdNum,
        allocated_room_number: roomFromGroup?.room_number || null,
        price: Math.max(0, parseFloat(addRoomRatePerNight) || 0),
        date: cursor.format('YYYY-MM-DD'),
        adults: Math.max(0, Number(addRoomAdults || 0)),
        children: Math.max(0, Number(addRoomChildren || 0)),
        room_sequence: nextSequence,
        guests: templateItem?.guests || [],
        checkout: false,
        upgraded_from_room_type_id: null,
      });
      cursor.add(1, 'day');
    }

    if (!newRowItems.length) {
      setAddRoomError('Could not build room nights for this stay. Please check the booking dates.');
      return;
    }

    const nextItems = [...bookingItems, ...newRowItems];
    // If the booking currently has no room rows, this is a restore — set rooms to 1.
    const nextTotalRooms = bookingItems.length === 0
      ? 1
      : (Number(formData.total_rooms || 1) || 1) + 1;
    try {
      setAddRoomSubmitting(true);
      setAddRoomError('');
      await persistRoomDetailsChanges(nextItems, {
        successMessage: 'Room added successfully.',
        nextTotalRooms,
      });
      setAddRoomDrawerOpen(false);
    } finally {
      setAddRoomSubmitting(false);
    }
  }, [
    isEditable,
    canAddRoomBeforeOrOnCheckIn,
    addRoomDateRuleMessage,
    addRoomSelectedTypeId,
    addRoomSelectedRoomId,
    addRoomAdults,
    addRoomChildren,
    addRoomRatePerNight,
    addRoomMaxGuests,
    formData.check_in_date,
    formData.check_out_date,
    formData.total_rooms,
    booking?.check_in_date,
    booking?.check_out_date,
    bookingItems,
    roomTypes,
    ratePlans,
    addRoomDisplayGroups,
    persistRoomDetailsChanges,
  ]);

  const handleOpenRemoveRoomConfirm = useCallback((group, totalDisplayRows = 0) => {
    if (!group?.items?.length) return;
    const targetItemIds = Array.from(
      new Set(
        ((group?.delete_scope_item_ids || group?.items || [])
          .map((it) => (typeof it === 'object' ? it?.id : it))
          .map((id) => Number(id || 0))
          .filter(Boolean))
      )
    );
    setRemoveRoomConfirmText('');
    setRemoveRoomConfirmDialog({ open: true, group, totalDisplayRows, targetItemIds });
  }, []);

  const handleRemoveRoomGroup = useCallback(async () => {
    const group = removeRoomConfirmDialog.group;
    const totalDisplayRows = removeRoomConfirmDialog.totalDisplayRows || 0;
    const typedDelete = String(removeRoomConfirmText || '').trim().toLowerCase();
    if (!isEditable) return;
    if (!group?.items?.length) return;
    if (typedDelete !== 'delete') return;
    if (totalDisplayRows <= 1) {
      setError('At least one room must remain in the booking.');
      setErrorDialogOpen(true);
      setRemoveRoomConfirmDialog({ open: false, group: null, totalDisplayRows: 0, targetItemIds: [] });
      setRemoveRoomConfirmText('');
      return;
    }

    const targetIdSet = new Set(
      (removeRoomConfirmDialog.targetItemIds || [])
        .map((id) => Number(id || 0))
        .filter(Boolean)
    );
    const removeKeys = new Set(
      (group.items || []).map((it, idx) => (it?.id != null ? `id_${it.id}` : `idx_${it?.index ?? idx}`))
    );
    const nextItems = bookingItems.filter((item, idx) => {
      if (targetIdSet.size > 0) {
        const itemId = Number(item?.id || 0);
        return !itemId || !targetIdSet.has(itemId);
      }
      return !removeKeys.has(item?.id != null ? `id_${item.id}` : `idx_${idx}`);
    });
    if (nextItems.length === bookingItems.length) return;

    const nextTotalRooms = Math.max(1, (Number(formData.total_rooms || 1) || 1) - 1);
    await persistRoomDetailsChanges(nextItems, {
      successMessage: 'Room removed successfully.',
      nextTotalRooms,
    });
    setRemoveRoomConfirmDialog({ open: false, group: null, totalDisplayRows: 0, targetItemIds: [] });
    setRemoveRoomConfirmText('');
  }, [
    isEditable,
    bookingItems,
    formData.total_rooms,
    persistRoomDetailsChanges,
    removeRoomConfirmDialog.group,
    removeRoomConfirmDialog.totalDisplayRows,
    removeRoomConfirmDialog.targetItemIds,
    removeRoomConfirmText,
  ]);

  // Calculate nights
  const nights = useMemo(() => {
    if (formData.check_in_date && formData.check_out_date) {
      return formData.check_out_date.diff(formData.check_in_date, 'days');
    }
    return 0;
  }, [formData.check_in_date, formData.check_out_date]);

  const stayDurationMeta = useMemo(
    () => getStayDurationMeta({
      check_in_date: formData.check_in_date?.isValid?.()
        ? formData.check_in_date.format('YYYY-MM-DD')
        : null,
      check_out_date: formData.check_out_date?.isValid?.()
        ? formData.check_out_date.format('YYYY-MM-DD')
        : null,
      nights: Math.max(0, nights),
    }),
    [formData.check_in_date, formData.check_out_date, nights]
  );

  // Booking amounts: exact to 2 decimal places (GST, discount, subtotal, total).
  const discountMaxAmount = useMemo(
    () =>
      roundEditBookingAmount(
        bookingItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
      ),
    [bookingItems]
  );

  const discountSummary = useMemo(
    () =>
      formatDiscountLabel({
        discountMode: formData.discount_mode,
        discountPercentage: formData.discount_percentage,
        discountAmount: formData.discount_amount,
      }),
    [formData.discount_mode, formData.discount_percentage, formData.discount_amount]
  );

  const calculationBreakdown = useMemo(() => {
    const breakdown = computeEditBookingBreakdown({
      bookingItems,
      discountMode: formData.discount_mode,
      discountPercentage: formData.discount_percentage,
      discountAmount: formData.discount_amount,
      gstPercentage: formData.gst_percentage,
      gstIncluded: formData.gst_included,
      taxInclusive: formData.tax_inclusive,
      booking,
    });
    return {
      subtotalBeforeDiscount: breakdown.subtotalBeforeDiscount.toFixed(2),
      discountAmount: breakdown.discountAmt.toFixed(2),
      subtotalAfterDiscount: breakdown.subtotalAfterDiscount.toFixed(2),
      gstAmount: breakdown.gstAmt.toFixed(2),
      total: breakdown.total.toFixed(2),
      taxInclusive: breakdown.taxInclusive,
      afterDiscountLabel: breakdown.afterDiscountLabel,
    };
  }, [
    booking,
    bookingItems,
    formData.discount_mode,
    formData.discount_percentage,
    formData.discount_amount,
    formData.gst_percentage,
    formData.gst_included,
    formData.tax_inclusive,
  ]);

  // Sync booking amount with calculated total (2 decimal places)
  useEffect(() => {
    const breakdown = computeEditBookingBreakdown({
      bookingItems,
      discountMode: formData.discount_mode,
      discountPercentage: formData.discount_percentage,
      discountAmount: formData.discount_amount,
      gstPercentage: formData.gst_percentage,
      gstIncluded: formData.gst_included,
      taxInclusive: formData.tax_inclusive,
      booking,
    });
    setFormData(prev => ({ ...prev, total_amount: breakdown.total }));
  }, [
    booking,
    bookingItems,
    formData.discount_mode,
    formData.discount_percentage,
    formData.discount_amount,
    formData.gst_percentage,
    formData.gst_included,
    formData.tax_inclusive,
  ]);

  const handleDiscountModeChange = (_, value) => {
    if (!value || !isEditable) return;
    setFormData((prev) => {
      if (value === 'amount') {
        const fromPct =
          discountMaxAmount > 0
            ? roundEditBookingAmount(discountMaxAmount * ((prev.discount_percentage || 0) / 100))
            : 0;
        return {
          ...prev,
          discount_mode: 'amount',
          discount_amount: fromPct,
          discount_percentage: 0,
        };
      }
      const pct =
        discountMaxAmount > 0 && prev.discount_amount > 0
          ? roundEditBookingAmount((prev.discount_amount / discountMaxAmount) * 100)
          : prev.discount_percentage || 0;
      return {
        ...prev,
        discount_mode: 'percentage',
        discount_percentage: Math.min(100, Math.max(0, pct)),
        discount_amount: 0,
      };
    });
  };

  const paymentSummary = useMemo(
    () => getPaymentSummaryData(booking, booking?.payments || []),
    [booking],
  );

  // Room total (live form) + persisted extra charges; balance = that total − paid.
  const paymentDisplayTotal = useMemo(() => {
    const roomTotal = parseFloat(calculationBreakdown.total) || 0;
    const extras = Number(paymentSummary?.extraChargesAmount) || 0;
    return roundEditBookingAmount(roomTotal + extras);
  }, [calculationBreakdown.total, paymentSummary?.extraChargesAmount]);

  const balanceDue = useMemo(() => {
    const totalPaidRaw = Number(paymentSummary?.paidAmount) || 0;
    const due = roundEditBookingAmount(Math.max(0, paymentDisplayTotal - totalPaidRaw));
    return Number.isFinite(due) ? due : 0;
  }, [paymentDisplayTotal, paymentSummary?.paidAmount]);

  // Fully paid is derived from balance: true when balance due is 0, false otherwise; switch is always readonly
  const isFullyPaidReadonly = true;
  useEffect(() => {
    const paid = balanceDue <= 0;
    setFormData((prev) => (prev.is_paid === paid ? prev : { ...prev, is_paid: paid }));
  }, [balanceDue]);

  const scheduledCheckInLabel = formData.check_in_date?.isValid?.()
    ? formatHotelDate(formData.check_in_date)
    : '—';
  const scheduledCheckOutLabel = formData.check_out_date?.isValid?.()
    ? formatHotelDate(formData.check_out_date)
    : '—';

  const actualCheckInDisplay = useMemo(() => resolveActualCheckInDisplay(booking), [booking]);
  const actualCheckOutDisplay = useMemo(() => resolveActualCheckOutDisplay(booking), [booking]);
  const actualCheckInMoment = useMemo(() => resolveActualCheckInMoment(booking), [booking]);
  const actualCheckOutMoment = useMemo(() => resolveActualCheckOutMoment(booking), [booking]);
  const canEditActualCheckInTime = useMemo(() => canEditActualCheckIn(booking), [booking]);
  const canEditActualCheckOutTime = useMemo(() => canEditActualCheckOut(booking), [booking]);
  const scheduledStayHint = useMemo(() => {
    if (!scheduledCheckInLabel || scheduledCheckInLabel === '—') return null;
    return `${scheduledCheckInLabel} → ${scheduledCheckOutLabel}`;
  }, [scheduledCheckInLabel, scheduledCheckOutLabel]);

  const [savingActualStay, setSavingActualStay] = useState(null);

  const handleSaveActualStayTime = async (field, momentValue) => {
    if (!normalizedBookingId || !momentValue?.isValid?.()) return;
    setSavingActualStay(field);
    try {
      setError(null);
      const iso = fromHotelPickerMomentToIso(momentValue, booking?.hotel_timezone);
      if (!iso) return;
      const payload = field === 'check_in_time'
        ? { check_in_time: iso }
        : { check_out_time: iso };
      const updated = await updateBooking(normalizedBookingId, payload);
      setBooking(updated);
      setSuccessMessage(
        field === 'check_in_time' ? 'Actual check-in updated.' : 'Actual check-out updated.'
      );
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update actual stay time.'));
      setErrorDialogOpen(true);
      throw err;
    } finally {
      setSavingActualStay(null);
    }
  };

  // Section-specific validation
  const validateSection = useCallback((section) => {
    const errors = {};
    if (section === 'booking') {
      if (!formData.check_in_date) errors.check_in_date = 'Check-in date is required';
      if (!formData.check_out_date) errors.check_out_date = 'Check-out date is required';
      // Same calendar day is valid (hourly / day-use stays); only earlier check-out is invalid.
      if (formData.check_in_date && formData.check_out_date && formData.check_out_date.isBefore(formData.check_in_date, 'day')) {
        errors.check_out_date = 'Check-out cannot be before check-in';
      }
      if (formData.pax_adults < 1) errors.pax_adults = 'At least 1 adult required';
      if (formData.pax_children < 0) errors.pax_children = 'Children cannot be negative';
      if (formData.total_rooms < 1) errors.total_rooms = 'At least 1 room required';
      if (formData.total_amount !== undefined && formData.total_amount !== '' && (parseFloat(formData.total_amount) || 0) < 0) {
        errors.total_amount = 'Booking amount cannot be negative';
      }
      const totalAmt = parseFloat(formData.total_amount) || 0;
      const advanceAmt = parseFloat(formData.advance_amount) || 0;
      if (totalAmt > 0 && advanceAmt > totalAmt) {
        errors.advance_amount = 'Advance amount cannot exceed total amount';
      }
    } else if (section === 'rooms') {
      const totalRooms = formData.total_rooms || 1;
      const n = formData.check_in_date && formData.check_out_date
        ? formData.check_out_date.diff(formData.check_in_date, 'days') : 0;
      // Same-day stays are billed as one room-night, matching the backend stay window.
      const chargeableNights = Math.max(1, n);
      const maxItems = totalRooms * chargeableNights;
      if (bookingItems.length > maxItems) {
        errors.bookingItems = `Room nights cannot exceed ${totalRooms} rooms × ${chargeableNights} night(s) = ${maxItems}. Currently ${bookingItems.length} item(s).`;
      }
      bookingItems.forEach((item, idx) => {
        const price = parseFloat(item.price);
        if (isNaN(price) || price < 0) errors[`price_${idx}`] = 'Valid rate required';
      });
    } else if (section === 'guest') {
      if (!selectedGuests?.length || !selectedGuests[0]?.id) errors.guest = 'Please assign a guest';
    }
    setValidationErrors(prev => ({ ...prev, ...errors }));
    const isValid = Object.keys(errors).length === 0;
    const firstErr = Object.values(errors)[0] || null;
    return [isValid, firstErr];
  }, [formData, selectedGuests, bookingItems]);

  const getSectionTitle = (section) => {
    const titles = { booking: 'Booking Information', rooms: 'Room Details', guest: 'Guest Information', payment: 'Payment Records' };
    return titles[section] || section;
  };

  const startPaymentCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraPermissionMessage(
        'Camera is not available. Turn off Camera upload to upload a receipt image from files.'
      );
      return;
    }
    try {
      if (paymentStream) {
        paymentStream.getTracks().forEach((track) => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setPaymentStream(mediaStream);
      setPaymentCameraOpen(true);
      setTimeout(() => {
        if (paymentReceiptVideoRef.current) {
          paymentReceiptVideoRef.current.srcObject = mediaStream;
          paymentReceiptVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (error) {
      setCameraPermissionMessage(
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings, or turn off Camera upload to choose a file.'
      );
    }
  };

  const stopPaymentCamera = () => {
    if (paymentStream) {
      paymentStream.getTracks().forEach((track) => track.stop());
      setPaymentStream(null);
    }
    setPaymentCameraOpen(false);
  };

  const capturePaymentReceipt = () => {
    if (paymentReceiptVideoRef.current && paymentReceiptCanvasRef.current) {
      const video = paymentReceiptVideoRef.current;
      const canvas = paymentReceiptCanvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setPaymentReceipt(imageData);
      stopPaymentCamera();
    }
  };

  const handlePaymentReceiptUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setPaymentReceipt(event.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please upload an image file.');
      }
    }
  };

  const handlePaymentReceiptClick = () => {
    if (paymentCameraUpload) {
      startPaymentCamera();
    } else {
      paymentReceiptFileInputRef.current?.click();
    }
  };

  const handleSaveSection = async () => {
    const section = saveConfirmDialog.section;
    if (!section || !bookingId) return;
    const [isValid, firstErr] = validateSection(section);
    if (!isValid) {
      setError(firstErr || `Please fix validation errors in ${getSectionTitle(section)}.`);
      setSaveConfirmDialog({ open: false, section: null });
      return;
    }
    setSavingSection(section);
    try {
      setError(null);
      let updateData = {};

      if (section === 'booking') {
        const formatDateForApi = (d) => {
          if (!d) return null;
          const m = moment.isMoment(d) ? d : moment(d);
          return m.isValid() ? m.format('YYYY-MM-DD') : null;
        };
        // Always send both dates when saving booking section (fallback to current booking so we never omit or clear them)
        const checkInStr = formatDateForApi(formData.check_in_date) ?? formatDateForApi(booking?.check_in_date);
        const checkOutStr = formatDateForApi(formData.check_out_date) ?? formatDateForApi(booking?.check_out_date);
        const normalizedTargetStatus = String(formData.status || '').toLowerCase();
        const normalizedCurrentStatus = String(booking?.status || '').toLowerCase();
        if (
          ['checked_in', 'partial_checkin'].includes(normalizedTargetStatus) &&
          normalizedTargetStatus !== normalizedCurrentStatus &&
          checkInStr &&
          moment().startOf('day').isBefore(moment(checkInStr, 'YYYY-MM-DD').startOf('day'))
        ) {
          setError(`Check-in is allowed only on or after ${moment(checkInStr).format('DD MMM YYYY')}, based on the hotel's local date/timezone.`);
          setErrorDialogOpen(true);
          setSaveConfirmDialog({ open: false, section: null });
          return;
        }
        if (
          ['checked_in', 'partial_checkin'].includes(normalizedTargetStatus) &&
          normalizedTargetStatus !== normalizedCurrentStatus
        ) {
          const allocationStats = getBookingRoomAllocationStats(
            bookingItems,
            formData.total_rooms || booking?.total_rooms || 1,
          );
          if (!allocationStats.hasAllocatedRooms) {
            setError('Allocate at least one room before checking in the booking.');
            setErrorDialogOpen(true);
            setSaveConfirmDialog({ open: false, section: null });
            return;
          }
          if (
            normalizedTargetStatus === 'checked_in' &&
            !allocationStats.allRoomsAllocated
          ) {
            setError(
              `Cannot mark as checked in: only ${allocationStats.allocatedRooms} of ${allocationStats.totalRooms} room(s) are allocated. Allocate all rooms or use partial check-in.`,
            );
            setErrorDialogOpen(true);
            setSaveConfirmDialog({ open: false, section: null });
            return;
          }
          if (
            normalizedTargetStatus === 'partial_checkin' &&
            allocationStats.allRoomsCheckedIn
          ) {
            setError(
              `All ${allocationStats.totalRooms} room(s) are already checked in. Use checked in instead of partial check-in.`,
            );
            setErrorDialogOpen(true);
            setSaveConfirmDialog({ open: false, section: null });
            return;
          }
          if (
            normalizedTargetStatus === 'partial_checkin' &&
            !allocationStats.hasCheckedInRooms &&
            allocationStats.allRoomsAllocated
          ) {
            setError(
              `All ${allocationStats.totalRooms} room(s) are allocated but none are checked in yet. Use the check-in drawer to check in selected rooms, or mark as checked in.`,
            );
            setErrorDialogOpen(true);
            setSaveConfirmDialog({ open: false, section: null });
            return;
          }
        }
        if (
          ['checked_out', 'partial_checkout'].includes(normalizedTargetStatus) &&
          normalizedTargetStatus !== normalizedCurrentStatus &&
          !['checked_in', 'partial_checkin', 'partial_checkout'].includes(normalizedCurrentStatus)
        ) {
          setError('Checkout or partial checkout is allowed only when booking is checked in.');
          setErrorDialogOpen(true);
          setSaveConfirmDialog({ open: false, section: null });
          return;
        }
        updateData = {
          ...(checkInStr != null && { check_in_date: checkInStr }),
          ...(checkOutStr != null && { check_out_date: checkOutStr }),
          ...(formData.booking_date_time?.isValid?.() && {
            created_at: fromHotelPickerMomentToIso(formData.booking_date_time, booking?.hotel_timezone),
          }),
          expected_arrival_time: formData.expected_arrival_time?.format?.('HH:mm') ?? (formData.expected_arrival_time ? moment(formData.expected_arrival_time).format('HH:mm') : undefined),
          pax_adults: formData.pax_adults,
          pax_children: formData.pax_children,
          total_rooms: formData.total_rooms,
          total_amount: parseFloat(formData.total_amount) || 0,
          advance_amount: parseFloat(formData.advance_amount) || 0,
          special_requests: formData.special_requests,
          internal_notes: formData.internal_notes,
          staff_notes: formData.staff_notes,
          discount_percentage: formData.discount_mode === 'amount' ? 0 : formData.discount_percentage,
          discount_amount:
            formData.discount_mode === 'amount'
              ? parseFloat(formData.discount_amount) || 0
              : 0,
          gst_percentage: formData.gst_percentage,
          gst_included: formData.gst_included,
          tax_inclusive: formData.tax_inclusive,
          is_paid: formData.is_paid,
          channel_id: formData.channel_id || null,
          channel_booking_code: formData.channel_booking_code || '',
          provider_booking_id: formData.provider_booking_id || '',
          booking_source: formData.booking_source || '',
          status: formData.status || undefined,
        };
        // Persist primary guest (first selected) with booking save
        const primary = selectedGuests?.length ? selectedGuests[0] : null;
        if (primary?.id) {
          const email = primary.email && primary.email !== '—' ? primary.email : '';
          const phone = primary.phone && primary.phone !== '—' ? primary.phone : '';
          updateData.guest = {
            id: primary.id,
            name: primary.name || '',
            email: email || undefined,
            phone: phone || undefined,
            country_code: primary.country_code || '+91',
          };
        }
      } else if (section === 'rooms') {
        updateData = buildRoomsUpdatePayload(bookingItems);
      } else if (section === 'guest') {
        const primary = selectedGuests?.length ? selectedGuests[0] : null;
        if (primary?.id) {
          const email = primary.email && primary.email !== '—' ? primary.email : '';
          const phone = primary.phone && primary.phone !== '—' ? primary.phone : '';
          updateData = {
            guest: {
              id: primary.id,
              name: primary.name || '',
              email: email || undefined,
              phone: phone || undefined,
              country_code: primary.country_code || '+91',
            },
          };
        }
      } else if (section === 'payment') {
        setError('Payment amount cannot be edited from modify booking.');
        setSaveConfirmDialog({ open: false, section: null });
        setSavingSection(null);
        return;
      }

      const updated = await updateBooking(bookingId, updateData);
      setSuccessMessage(`${getSectionTitle(section)} saved successfully!`);
      setSaveConfirmDialog({ open: false, section: null });
      if (section === 'payment') return;
      setValidationErrors({});
      // Update from response immediately so dates/booking info show without waiting for refetch
      if (updated && section === 'booking') {
        setBooking(updated);
        setFormData((prev) => ({
          ...prev,
          check_in_date: updated.check_in_date ? moment(updated.check_in_date) : null,
          check_out_date: updated.check_out_date ? moment(updated.check_out_date) : null,
          booking_date_time: updated.created_at
            ? toHotelPickerMoment(updated.created_at, updated.hotel_timezone || booking?.hotel_timezone)
            : null,
          expected_arrival_time: updated.expected_arrival_time
            ? moment(updated.expected_arrival_time, 'HH:mm:ss')
            : null,
          pax_adults: updated.pax_adults ?? prev.pax_adults,
          pax_children: updated.pax_children ?? prev.pax_children,
          total_rooms: updated.total_rooms ?? prev.total_rooms,
          total_amount: updated.total_amount ?? prev.total_amount,
          advance_amount: updated.advance_amount ?? prev.advance_amount,
          special_requests: updated.special_requests ?? prev.special_requests,
          internal_notes: updated.internal_notes ?? prev.internal_notes,
          staff_notes: updated.staff_notes ?? prev.staff_notes,
          discount_percentage: updated.discount_percentage ?? prev.discount_percentage,
          discount_amount: updated.discount_amount ?? prev.discount_amount,
          discount_mode:
            (updated.discount_amount ?? prev.discount_amount) > 0
            && !(updated.discount_percentage ?? prev.discount_percentage)
              ? 'amount'
              : 'percentage',
          gst_percentage: updated.gst_percentage ?? prev.gst_percentage,
          gst_included: updated.gst_included ?? prev.gst_included,
          tax_inclusive: updated.tax_inclusive ?? updated.payment_summary?.tax_inclusive ?? prev.tax_inclusive,
          is_paid: updated.is_paid ?? prev.is_paid,
          channel_id: updated.channel?.id ?? prev.channel_id,
          channel_booking_code: updated.channel_booking_code ?? prev.channel_booking_code,
          provider_booking_id: updated.provider_booking_id ?? prev.provider_booking_id,
          booking_source: updated.booking_source ?? prev.booking_source,
          status: updated.status ?? prev.status,
        }));
        const items = updated.items || updated.booking_items || [];
        if (items.length) {
          setBookingItems(items.map(item => ({
            id: item.id,
            room_type_id: item.room_type?.id,
            room_type_name: item.room_type?.name,
            room_type: item.room_type ? { id: item.room_type.id, name: item.room_type.name, image_url: item.room_type.image_url, image: item.room_type.image } : undefined,
            rate_plan_id: item.rate_plan?.id,
            rate_plan_name: item.rate_plan?.name,
            allocated_room_id: item.allocated_room?.id,
            allocated_room_number: item.allocated_room?.room_number || item.allocated_room?.number,
            price: item.price || 0,
            date: item.date,
            adults: item.adults ?? 1,
            children: item.children ?? 0,
            room_sequence: item.room_sequence || 1,
            guests: item.guests || [],
            checked_in: item.checked_in ?? false,
            checkout: item.checkout ?? false,
            upgraded_from_room_type_id: item.upgraded_from_room_type_id ?? item.upgraded_from_room_type?.id ?? null,
          })));
        }
      }
      await fetchBookingData();
    } catch (err) {
      console.error('Error saving:', err);
      const errMsg = getErrorMessage(err, `Failed to save ${getSectionTitle(section)}`);
      setError(errMsg);
      if (section === 'booking') {
        setResetBookingDatesOnErrorClose(true);
      }
      // Close save confirmation on failure so user returns to edit screen after acknowledging error.
      setSaveConfirmDialog({ open: false, section: null });
      setErrorDialogOpen(true); // Show error in modal popup
    } finally {
      setSavingSection(null);
    }
  };

  // Total payments must always be >= advance amount; block delete if it would violate that
  const deletePaymentBlockedByAdvance = useMemo(() => {
    const payment = deleteConfirmDialog.payment;
    if (!payment || !deleteConfirmDialog.open) return null;
    const totalPaid = parseFloat(booking?.payment_summary?.total_paid) || 0;
    const advanceAmount = parseFloat(formData.advance_amount ?? booking?.advance_amount) || 0;
    const paymentAmount = parseFloat(payment.amount) || 0;
    const afterDelete = totalPaid - paymentAmount;
    if (afterDelete < advanceAmount) return { advanceAmount, paymentAmount };
    return null;
  }, [deleteConfirmDialog.open, deleteConfirmDialog.payment, booking?.payment_summary?.total_paid, formData.advance_amount, booking?.advance_amount]);

  const handleDeletePayment = async () => {
    const payment = deleteConfirmDialog.payment;
    if (!payment?.id || !bookingId) return;
    if (deletePaymentBlockedByAdvance) {
      setError(
        `Total payments cannot be less than the advance amount (₹${(deletePaymentBlockedByAdvance.advanceAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}). ` +
        `Deleting this payment (₹${(deletePaymentBlockedByAdvance.paymentAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}) would make total payments too low.`
      );
      return;
    }
    setDeletingPayment(true);
    try {
      await updateBooking(bookingId, { delete_payments: [payment.id] });
      setSuccessMessage('Payment deleted successfully.');
      setDeleteConfirmDialog({ open: false, payment: null });
      await fetchBookingData();
    } catch (err) {
      console.error('Error deleting payment:', err);
      const errMsg = getErrorMessage(err, 'Failed to delete payment');
      setError(errMsg);
      setErrorDialogOpen(true);
    } finally {
      setDeletingPayment(false);
    }
  };

  // Check if booking is editable (cancelled bookings can only change status to re-enable)
  const isEditable = useMemo(() => {
    if (!booking) return false;
    const status = booking.status?.toLowerCase();
    return status !== 'cancelled';
  }, [booking]);

  // When cancelled, allow editing the status dropdown so user can re-enable the booking
  const canReenableViaStatus = useMemo(() => booking?.status?.toLowerCase() === 'cancelled', [booking]);
  const bookingRoomAllocationStats = useMemo(
    () => getBookingRoomAllocationStats(
      bookingItems,
      formData.total_rooms || booking?.total_rooms || 1,
    ),
    [bookingItems, formData.total_rooms, booking?.total_rooms],
  );
  const canCheckInOnDate = useMemo(() => {
    const checkInSource = formData.check_in_date || booking?.check_in_date;
    if (!checkInSource) return true;
    const checkInDate = moment.isMoment(checkInSource)
      ? checkInSource.clone().startOf('day')
      : moment(checkInSource).startOf('day');
    if (!checkInDate.isValid()) return true;
    return !moment().startOf('day').isBefore(checkInDate);
  }, [formData.check_in_date, booking?.check_in_date]);
  const canSetFullCheckedInStatus = useMemo(
    () => canCheckInOnDate
      && bookingRoomAllocationStats.allRoomsAllocated,
    [canCheckInOnDate, bookingRoomAllocationStats],
  );
  const canSetPartialCheckInStatus = useMemo(
    () => canCheckInOnDate
      && bookingRoomAllocationStats.hasCheckedInRooms
      && !bookingRoomAllocationStats.allRoomsCheckedIn,
    [canCheckInOnDate, bookingRoomAllocationStats],
  );
  const canSetPartialCheckoutStatus = useMemo(() => {
    const currentStatus = String(booking?.status || '').toLowerCase();
    return ['checked_in', 'partial_checkin', 'partial_checkout'].includes(currentStatus);
  }, [booking?.status]);
  const canSetCheckedOutStatus = useMemo(() => {
    const currentStatus = String(booking?.status || '').toLowerCase();
    return ['checked_in', 'partial_checkin', 'partial_checkout', 'checked_out'].includes(currentStatus);
  }, [booking?.status]);
  // Check-out date editable when status is confirmed, checked_in or partial_checkin
  const canEditDates = useMemo(() => {
    const status = String(booking?.status || '').toLowerCase();
    return ['confirmed', 'checked_in', 'partial_checkin'].includes(status);
  }, [booking?.status]);
  // Check-in date editable only for confirmed bookings; must be today or future (no back date)
  const canEditCheckInDate = useMemo(() => {
    return String(booking?.status || '').toLowerCase() === 'confirmed';
  }, [booking?.status]);
  const addRoomTooltipTitle = canAddRoomBeforeOrOnCheckIn
    ? 'Add Room'
    : addRoomDateRuleMessage;

  // Group booking items by room sequence (for Room Details display and summary cards)
  const groupedBookingItems = useMemo(() => {
    const getDateKey = (it) => {
      if (!it?.date) return '';
      return moment.isMoment(it.date) ? it.date.format('YYYY-MM-DD') : String(it.date).slice(0, 10);
    };

    // First pass: base grouping by room_type + sequence.
    const baseGroups = {};
    bookingItems.forEach((item, index) => {
      const baseKey = `${item.room_type_id || 'unknown'}-${item.room_sequence ?? 1}`;
      if (!baseGroups[baseKey]) {
        baseGroups[baseKey] = {
          key: baseKey,
          room_type_name: item.room_type_name,
          room_sequence: item.room_sequence ?? 1,
          items: [],
        };
      }
      baseGroups[baseKey].items.push({ ...item, index });
    });

    // Second pass: when same-date collisions exist within a base group, split into
    // separate logical rows so each "room stream" remains visible and actionable.
    const splitGroups = [];
    Object.values(baseGroups).forEach((group) => {
      const items = [...(group.items || [])].sort((a, b) => {
        const aDate = getDateKey(a);
        const bDate = getDateKey(b);
        if (aDate !== bDate) return aDate.localeCompare(bDate);
        const aRoom = String(a?.allocated_room_number || a?.allocated_room_id || '');
        const bRoom = String(b?.allocated_room_number || b?.allocated_room_id || '');
        if (aRoom !== bRoom) return aRoom.localeCompare(bRoom);
        return (a?.id || 0) - (b?.id || 0);
      });

      const dateCounts = {};
      items.forEach((it) => {
        const d = getDateKey(it);
        dateCounts[d] = (dateCounts[d] || 0) + 1;
      });
      const hasSameDateCollision = Object.values(dateCounts).some((count) => count > 1);
      if (!hasSameDateCollision) {
        splitGroups.push(group);
        return;
      }

      // Greedy line-assignment: each split line keeps at most one item per date.
      const lines = [];
      items.forEach((it) => {
        const d = getDateKey(it);
        let assigned = false;
        for (const line of lines) {
          if (!line.dates.has(d)) {
            line.items.push(it);
            line.dates.add(d);
            assigned = true;
            break;
          }
        }
        if (!assigned) {
          lines.push({
            dates: new Set([d]),
            items: [it],
          });
        }
      });

      lines.forEach((line, idx) => {
        splitGroups.push({
          ...group,
          key: `${group.key}__line_${idx + 1}`,
          items: line.items,
        });
      });
    });

    return splitGroups.sort((a, b) => {
      const byType = (a.room_type_name || '').localeCompare(b.room_type_name || '', undefined, { sensitivity: 'base' });
      return byType !== 0 ? byType : (a.room_sequence || 0) - (b.room_sequence || 0);
    });
  }, [bookingItems]);

  // Group booking items by date for per-day display
  const groupedByDate = useMemo(() => {
    const byDate = {};
    bookingItems.forEach((item, index) => {
      const dateKey = item.date || '';
      if (!byDate[dateKey]) byDate[dateKey] = [];
      byDate[dateKey].push({ ...item, index });
    });
    return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
  }, [bookingItems]);

  // Styles
  const sectionHeaderStyle = {
    backgroundColor: alpha(EDIT_BOOKING_ACCENT, isDark ? 0.08 : 0.05),
    borderLeft: `4px solid ${EDIT_BOOKING_ACCENT}`,
  };

  const cardStyle = {
    backgroundColor: surface.surface,
    border: `1px solid ${surface.surfaceBorder}`,
    borderRadius: 3,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : '0 2px 12px rgba(0,0,0,0.06)',
    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
    overflow: 'hidden',
  };

  const walkinFieldSx = (error) => getEditBookingFieldSx(isDark, error, theme);
  const selectWalkinSx = getEditBookingSelectSx(isDark);

  if (loading) {
    return <EditBookingPageSkeleton />;
  }

  if (error && !booking) {
    return (
      <Box sx={getEditBookingPageShellSx(isDark)}>
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Edit Booking"
            backFallbackPath="/booking/list/"
          />
        </Box>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: surface.surface,
            border: `1px solid ${surface.surfaceBorder}`,
            borderRadius: 2,
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
          <Button
            startIcon={<Refresh />}
            onClick={() => fetchBookingData()}
            sx={{ color: EDIT_BOOKING_ACCENT }}
          >
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  const statusColor = getEditBookingStatusColor(booking?.status, isDark);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={getEditBookingPageShellSx(isDark)}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Edit Booking"
            backFallbackPath="/booking/list/"
            navLinks={[
              { url: '/booking/list/', label: 'Booking List' },
              { url: normalizedBookingId ? `/booking/detail/${normalizedBookingId}/` : '/booking/list/', label: 'View Detail' },
            ]}
          />
        </Box>

        {/* Hero Card */}
        <Paper elevation={0} sx={getEditBookingHeroPaperSx(isDark)}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
              <ChannelImage
                channel={booking?.channel?.image_path ? booking.channel : null}
                size={40}
                isPMS={!booking?.channel?.image_path}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: isDark ? surface.text : '#444',
                    display: 'block',
                    mb: 0.25,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  }}
                >
                  {booking?.code}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                  <Typography component="span" variant="body2" sx={{ color: isDark ? surface.subtleText : '#666' }}>
                    {scheduledCheckInLabel}
                  </Typography>
                  <Typography component="span" variant="body2" sx={{ color: isDark ? surface.subtleText : '#666', mx: 0.25 }}>
                    →
                  </Typography>
                  <Typography component="span" variant="body2" sx={{ color: isDark ? surface.subtleText : '#666' }}>
                    {scheduledCheckOutLabel}
                  </Typography>
                  <Typography component="span" variant="body2" sx={{ color: isDark ? surface.subtleText : '#666', ml: 0.25 }}>
                    · {stayDurationMeta.summaryText}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={booking?.status?.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      backgroundColor: statusColor.bg,
                      color: statusColor.color,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      height: 22,
                      fontSize: '0.7rem',
                    }}
                  />
                  {(formData.booking_source || booking?.booking_source) && (
                    <Chip
                      label={(formData.booking_source || booking.booking_source).replace(/_/g, ' ')}
                      size="small"
                      variant="outlined"
                      sx={getEditBookingOutlinedChipSx(isDark)}
                    />
                  )}
                  {((formData.booking_source || booking?.booking_source) || '').toLowerCase() === 'ota' && (booking?.channel?.name || channels.find(c => c.id === formData.channel_id)?.name) && (
                    <Chip
                      label={booking?.channel?.name || channels.find(c => c.id === formData.channel_id)?.name}
                      size="small"
                      variant="outlined"
                      sx={getEditBookingOutlinedChipSx(isDark)}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Alert Messages */}
        {(error || !isEditable) && (
          <Box sx={{ mb: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {!isEditable && (
              <Alert severity="warning">
                {canReenableViaStatus
                  ? 'This booking is cancelled. Change the status above to re-enable editing, then save.'
                  : `This booking cannot be edited because it is ${booking?.status?.replace(/_/g, ' ') || 'restricted'}.`}
              </Alert>
            )}
          </Box>
        )}

        {/* Main Content - Two columns: left = Booking/Rooms/Guest cards, right = Payment */}
        <Paper elevation={0} sx={getEditBookingMainPaperSx(isDark)}>
          <Grid container spacing={3}>
            {/* Booking row: left = inputs + summary card + amount row (same row as Payment Summary) */}
            <Grid item xs={12} lg={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'flex-start' },
                  gap: 1.5,
                }}
              >
                {/* Left: Check-in + booking date/time; right: Check-out + Adults + Children */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label={
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            Check-in Date
                            <InfoTooltip title="Date when the guest will arrive. Editable only for confirmed bookings; must be today or a future date." isDark={isDark} />
                          </Box>
                        }
                        value={formData.check_in_date}
                        onChange={(date) => {
                          setFormData(prev => {
                            if (!date || !date.isValid()) return { ...prev, check_in_date: date };
                            const prevOut = prev.check_out_date;
                            const newCheckOut = (
                              prevOut && prevOut.isValid() && prevOut.isAfter(date)
                            )
                              ? prevOut
                              : date.clone().add(1, 'days');
                            return { ...prev, check_in_date: date, check_out_date: newCheckOut };
                          });
                          setValidationErrors(prev => ({ ...prev, check_in_date: undefined, check_out_date: undefined }));
                        }}
                        disabled={!isEditable || !canEditCheckInDate}
                        minDate={moment().startOf('day')}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!validationErrors.check_in_date,
                            helperText: validationErrors.check_in_date,
                            sx: walkinFieldSx(!!validationErrors.check_in_date),
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label={
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            Check-out Date
                            <InfoTooltip title="Date when the guest will depart. Same day as check-in is allowed for hourly / day-use stays." isDark={isDark} />
                          </Box>
                        }
                        value={formData.check_out_date}
                        onChange={(date) => { handleFormChange('check_out_date', date); setValidationErrors(prev => ({ ...prev, check_out_date: undefined })); }}
                        disabled={!isEditable || !canEditDates}
                        minDate={formData.check_in_date && formData.check_in_date.isValid() ? formData.check_in_date.clone() : undefined}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!validationErrors.check_out_date,
                            helperText: validationErrors.check_out_date,
                            sx: walkinFieldSx(!!validationErrors.check_out_date),
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DateTimePicker
                        label={
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            Booking Date &amp; Time
                            <InfoTooltip title="When the booking was created or recorded in the system." isDark={isDark} />
                          </Box>
                        }
                        value={formData.booking_date_time}
                        onChange={(value) => {
                          handleFormChange('booking_date_time', value);
                          setValidationErrors(prev => ({ ...prev, booking_date_time: undefined }));
                        }}
                        disabled={!isEditable}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!validationErrors.booking_date_time,
                            helperText: validationErrors.booking_date_time,
                            sx: walkinFieldSx(!!validationErrors.booking_date_time),
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
                {/* Right: Fancy booking card — compact, aligned 3-column rows */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Card
                    elevation={0}
                    sx={{
                      ...getEditBookingSectionCardSx(isDark),
                      width: '100%',
                      p: 1.5,
                      pb: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                        gap: { xs: 1.25, sm: 1.5 },
                        alignItems: 'start',
                      }}
                    >
                      <EditableActualStayField
                        label="Actual check-in"
                        display={actualCheckInDisplay}
                        momentValue={actualCheckInMoment}
                        scheduledHint={scheduledStayHint}
                        canEdit={canEditActualCheckInTime}
                        kind="checkin"
                        isDark={isDark}
                        accent={EDIT_BOOKING_ACCENT}
                        saving={savingActualStay === 'check_in_time'}
                        onSave={(next) => handleSaveActualStayTime('check_in_time', next)}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: { xs: 'flex-start', sm: 'center' },
                          pt: { sm: 2.25 },
                          minWidth: { sm: 56 },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <NightsStay sx={{ fontSize: 18, color: EDIT_BOOKING_ACCENT }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: isDark ? surface.text : '#333' }}
                          >
                            {stayDurationMeta.chipLabel}
                          </Typography>
                        </Box>
                      </Box>
                      <EditableActualStayField
                        label="Actual check-out"
                        display={actualCheckOutDisplay}
                        momentValue={actualCheckOutMoment}
                        scheduledHint={scheduledStayHint}
                        canEdit={canEditActualCheckOutTime}
                        kind="checkout"
                        isDark={isDark}
                        accent={EDIT_BOOKING_ACCENT}
                        saving={savingActualStay === 'check_out_time'}
                        onSave={(next) => handleSaveActualStayTime('check_out_time', next)}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: 1.5,
                        mt: 1.25,
                        pt: 1.25,
                        borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Hotel sx={{ fontSize: 18, color: EDIT_BOOKING_ACCENT, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666', display: 'block', lineHeight: 1.2 }}>
                            Rooms
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? surface.text : '#333', lineHeight: 1.3 }}>
                            {formData.total_rooms ?? ''}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'center' }, gap: 1, minWidth: 0 }}>
                        <Person sx={{ fontSize: 18, color: EDIT_BOOKING_ACCENT, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666', display: 'block', lineHeight: 1.2 }}>
                            Adults
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? surface.text : '#333', lineHeight: 1.3 }}>
                            {formData.pax_adults ?? ''}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <ChildCare sx={{ fontSize: 18, color: EDIT_BOOKING_ACCENT, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666', display: 'block', lineHeight: 1.2 }}>
                            Children
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? surface.text : '#333', lineHeight: 1.3 }}>
                            {formData.pax_children ?? ''}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Box>
                {/* Left side: Booking Amount, Advance amount, GST Included, GST % — same row as Payment Summary */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Booking Amount (₹)
                          <InfoTooltip title="Total payable amount (read-only, derived from room nights and rates)." isDark={isDark} />
                        </Box>
                      }
                      type="number"
                      value={formData.total_amount ?? ''}
                      fullWidth
                      error={!!validationErrors.total_amount}
                      helperText={validationErrors.total_amount}
                      inputProps={{ min: 0, step: 0.01, readOnly: true }}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      sx={{
                        ...walkinFieldSx(!!validationErrors.total_amount),
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                        '& .MuiInputBase-input.MuiInput-input': { cursor: 'default' },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Advance Amount (₹)
                          <InfoTooltip title="Amount already paid. Must be ≤ total amount." isDark={isDark} />
                        </Box>
                      }
                      type="number"
                      value={formData.advance_amount ?? ''}
                      onChange={(e) => { handleFormChange('advance_amount', e.target.value); setValidationErrors(prev => ({ ...prev, advance_amount: undefined })); }}
                      disabled={!isEditable}
                      fullWidth
                      error={!!validationErrors.advance_amount}
                      helperText={validationErrors.advance_amount}
                      inputProps={{ min: 0, step: 0.01, max: parseFloat(formData.total_amount) || undefined }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                        endAdornment: isEditable && (
                          <InputAdornment position="end" sx={{ mr: -0.5, maxHeight: 'none', alignSelf: 'center' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 0,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const v = parseFloat(formData.advance_amount) || 0;
                                  const maxVal = parseFloat(formData.total_amount) || Infinity;
                                  handleFormChange('advance_amount', Math.min(maxVal, v + 100));
                                  setValidationErrors(prev => ({ ...prev, advance_amount: undefined }));
                                }}
                                disabled={!isEditable}
                                sx={{
                                  p: 0,
                                  minWidth: 24,
                                  minHeight: 0,
                                  height: 17,
                                  borderRadius: '2px',
                                  color: isDark ? surface.subtleText : '#666',
                                  '&:hover': { color: isDark ? surface.text : '#333' },
                                }}
                                aria-label="Increase amount"
                              >
                                <KeyboardArrowUp sx={{ fontSize: 16, display: 'block' }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const v = parseFloat(formData.advance_amount) || 0;
                                  handleFormChange('advance_amount', Math.max(0, v - 100));
                                  setValidationErrors(prev => ({ ...prev, advance_amount: undefined }));
                                }}
                                disabled={!isEditable}
                                sx={{
                                  p: 0,
                                  minWidth: 24,
                                  minHeight: 0,
                                  height: 17,
                                  borderRadius: '2px',
                                  color: isDark ? surface.subtleText : '#666',
                                  '&:hover': { color: isDark ? surface.text : '#333' },
                                }}
                                aria-label="Decrease amount"
                              >
                                <KeyboardArrowDown sx={{ fontSize: 16, display: 'block' }} />
                              </IconButton>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        ...walkinFieldSx(!!validationErrors.advance_amount),
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!formData.gst_included}
                            onChange={(e) => handleFormChange('gst_included', e.target.checked)}
                            disabled={!isEditable}
                            color="primary"
                          />
                        }
                        label={
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Typography component="span" variant="body2" sx={{ color: isDark ? surface.text : '#333' }}>GST Included</Typography>
                            <InfoTooltip title="Enable if the booking amount already includes GST. GST % will be used to calculate the tax component." isDark={isDark} />
                          </Box>
                        }
                      />
                      {formData.gst_included ? (
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={formData.tax_inclusive ? 'inclusive' : 'exclusive'}
                          onChange={(_, value) => {
                            if (value != null) handleFormChange('tax_inclusive', value === 'inclusive');
                          }}
                          disabled={!isEditable}
                          sx={{
                            mt: 0.5,
                            ml: 0.5,
                            ...reviewToggleGroupSx(isDark, isDark ? '#444' : '#e0e0e0'),
                            ...reviewTaxModeToggleSx(isDark, true),
                          }}
                        >
                          <ToggleButton value="inclusive">Inclusive</ToggleButton>
                          <ToggleButton value="exclusive">Exclusive</ToggleButton>
                        </ToggleButtonGroup>
                      ) : null}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          GST %
                          <InfoTooltip title="GST rate (0–100%). Used to split amount into base + tax when GST Included is on." isDark={isDark} />
                        </Box>
                      }
                      type="number"
                      value={formData.gst_percentage ?? ''}
                      onChange={(e) => handleFormChange('gst_percentage', e.target.value)}
                      disabled={!isEditable || !formData.gst_included}
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      InputProps={{
                        endAdornment: isEditable && formData.gst_included && (
                          <InputAdornment position="end" sx={{ mr: -0.5, maxHeight: 'none', alignSelf: 'center' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 0,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const v = parseFloat(formData.gst_percentage) || 0;
                                  handleFormChange('gst_percentage', Math.min(100, v + 1));
                                }}
                                disabled={!isEditable || !formData.gst_included}
                                sx={{
                                  p: 0,
                                  minWidth: 24,
                                  minHeight: 0,
                                  height: 17,
                                  borderRadius: '2px',
                                  color: isDark ? surface.subtleText : '#666',
                                  '&:hover': { color: isDark ? surface.text : '#333' },
                                }}
                                aria-label="Increase GST"
                              >
                                <KeyboardArrowUp sx={{ fontSize: 16, display: 'block' }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const v = parseFloat(formData.gst_percentage) || 0;
                                  handleFormChange('gst_percentage', Math.max(0, v - 1));
                                }}
                                disabled={!isEditable || !formData.gst_included}
                                sx={{
                                  p: 0,
                                  minWidth: 24,
                                  minHeight: 0,
                                  height: 17,
                                  borderRadius: '2px',
                                  color: isDark ? surface.subtleText : '#666',
                                  '&:hover': { color: isDark ? surface.text : '#333' },
                                }}
                                aria-label="Decrease GST"
                              >
                                <KeyboardArrowDown sx={{ fontSize: 16, display: 'block' }} />
                              </IconButton>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        ...walkinFieldSx(false),
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      }}
                    />
                  </Grid>
                </Grid>
                {/* Discount bar (sx 9) + Fully paid toggle (sx 3) — same row */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={9}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.2)'}`,
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          gap: 0.75,
                          flexWrap: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: isDark ? surface.text : '#333', flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                          Discount
                        </Typography>
                        <InfoTooltip title="Apply discount as a percentage of subtotal or enter a fixed amount in ₹." isDark={isDark} />
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={formData.discount_mode}
                          onChange={handleDiscountModeChange}
                          disabled={!isEditable}
                          sx={{
                            ...reviewToggleGroupSx(isDark, isDark ? '#444' : '#e0e0e0'),
                            width: 'auto',
                            flexShrink: 0,
                            '& .MuiToggleButton-root': {
                              minWidth: 36,
                              width: 36,
                              px: 0.5,
                              py: 0.35,
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              flex: 'none',
                            },
                          }}
                        >
                          <ToggleButton value="percentage" aria-label="Discount percentage">
                            <PercentIcon sx={{ fontSize: 16 }} />
                          </ToggleButton>
                          <ToggleButton value="amount" aria-label="Discount amount">
                            ₹
                          </ToggleButton>
                        </ToggleButtonGroup>
                        <Chip
                          size="small"
                          label={discountSummary}
                          sx={{
                            ml: 'auto',
                            flexShrink: 0,
                            height: 24,
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            background: `linear-gradient(135deg, ${EDIT_BOOKING_ACCENT} 0%, #3ab896 100%)`,
                            color: '#000',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {formData.discount_mode === 'percentage' ? (
                          <Box sx={{ flex: 1, minWidth: 120 }}>
                            <Slider
                              value={Number(formData.discount_percentage ?? 0)}
                              onChange={(_, value) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  discount_percentage: value,
                                  discount_amount: 0,
                                  discount_mode: 'percentage',
                                }));
                              }}
                              min={0}
                              max={100}
                              step={1}
                              disabled={!isEditable}
                              valueLabelDisplay="auto"
                              valueLabelFormat={(v) => `${v}%`}
                              sx={{
                                color: EDIT_BOOKING_ACCENT,
                                '& .MuiSlider-thumb': { width: 20, height: 20 },
                                '& .MuiSlider-rail': { opacity: 0.4 },
                              }}
                            />
                          </Box>
                        ) : (
                          <EditBookingDiscountAmountField
                            value={parseFloat(formData.discount_amount) || 0}
                            onChange={(amount) => {
                              setFormData((prev) => ({
                                ...prev,
                                discount_amount: amount,
                                discount_percentage: 0,
                                discount_mode: 'amount',
                              }));
                            }}
                            maxAmount={discountMaxAmount}
                            disabled={!isEditable}
                            isDark={isDark}
                            fieldSx={walkinFieldSx(false)}
                          />
                        )}
                      </Box>
                      {(formData.discount_percentage > 0 || formData.discount_amount > 0) && (
                        <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666', display: 'block', mt: 0.5 }}>
                          − ₹{parseFloat(calculationBreakdown.discountAmount || 0).toFixed(2)} off
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Tooltip title="Fully paid is set automatically from balance due (read-only).">
                      <span>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!formData.is_paid}
                              onChange={(e) => handleFormChange('is_paid', e.target.checked)}
                              disabled={!isEditable || isFullyPaidReadonly}
                              color="primary"
                            />
                          }
                          label={
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: isDark ? surface.text : '#333' }}>
                                Fully paid
                              </Typography>
                              <InfoTooltip title="Derived from balance due: Yes when balance is zero, No otherwise. Read-only." isDark={isDark} />
                            </Box>
                          }
                        />
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
                {/* Row: Channel, Channel booking code, Provider booking id, Booking source */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth sx={selectWalkinSx}>
                      <InputLabel>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Channel
                          <InfoTooltip title="OTA or channel (e.g. Booking.com). Leave empty for direct bookings." isDark={isDark} />
                        </Box>
                      </InputLabel>
                      <Select
                        label="Channel"
                        value={formData.channel_id ?? ''}
                        onChange={(e) => handleFormChange('channel_id', e.target.value || null)}
                        disabled={!isEditable}
                        displayEmpty
                        renderValue={(v) => {
                          if (!v) return '—';
                          const ch = channels.find(c => c.id === v);
                          return ch?.name || v;
                        }}
                      >
                        <MenuItem value="">—</MenuItem>
                        {channels.map((ch) => (
                          <MenuItem key={ch.id} value={ch.id}>{ch.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Channel booking code
                          <InfoTooltip title="Confirmation ID from the channel (e.g. BC-12345)." isDark={isDark} />
                        </Box>
                      }
                      value={formData.channel_booking_code ?? ''}
                      onChange={(e) => handleFormChange('channel_booking_code', e.target.value)}
                      disabled={!isEditable}
                      fullWidth
                      sx={walkinFieldSx(false)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Provider booking id
                          <InfoTooltip title="External system or provider's internal reference ID." isDark={isDark} />
                        </Box>
                      }
                      value={formData.provider_booking_id ?? ''}
                      onChange={(e) => handleFormChange('provider_booking_id', e.target.value)}
                      disabled={!isEditable}
                      fullWidth
                      sx={walkinFieldSx(false)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth sx={selectWalkinSx}>
                      <InputLabel>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Booking source
                          <InfoTooltip title="How the booking was received (direct, OTA, walk-in, etc.)." isDark={isDark} />
                        </Box>
                      </InputLabel>
                      <Select
                        label="Booking source"
                        value={formData.booking_source ?? ''}
                        onChange={(e) => handleFormChange('booking_source', e.target.value)}
                        disabled={!isEditable}
                        displayEmpty
                        renderValue={(v) => {
                          if (!v) return '';
                          const opt = BOOKING_SOURCE_OPTIONS.find((o) => o.value === v);
                          return opt ? opt.label : v.replace(/_/g, ' ');
                        }}
                      >
                        <MenuItem value="">
                          <em>—</em>
                        </MenuItem>
                        {BOOKING_SOURCE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                {/* Row: sx6 = Current booking status + Expected arrival, sx6 = Assign guest */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth sx={selectWalkinSx}>
                          <InputLabel>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              Current booking status
                              <InfoTooltip title="Lifecycle: Pending → Confirmed → Checked in → Checked out." isDark={isDark} />
                            </Box>
                          </InputLabel>
                          <Select
                            label="Current booking status"
                            value={formData.status || ''}
                            onChange={(e) => handleFormChange('status', e.target.value)}
                            disabled={!isEditable && !canReenableViaStatus}
                          >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="confirmed">Confirmed</MenuItem>
                            <MenuItem value="checked_in" disabled={!canSetFullCheckedInStatus}>
                              Checked in
                            </MenuItem>
                            <MenuItem value="partial_checkin" disabled={!canSetPartialCheckInStatus}>
                              Partial check-in
                            </MenuItem>
                            <MenuItem value="partial_checkout" disabled={!canSetPartialCheckoutStatus}>
                              Partial checkout
                            </MenuItem>
                            <MenuItem value="checked_out" disabled={!canSetCheckedOutStatus}>
                              Checked out
                            </MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="no_show">No show</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TimePicker
                          label={
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              Expected Arrival
                              <InfoTooltip title="Approximate time the guest plans to arrive." isDark={isDark} />
                            </Box>
                          }
                          value={formData.expected_arrival_time}
                          onChange={(time) => handleFormChange('expected_arrival_time', time)}
                          disabled={!isEditable}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              sx: walkinFieldSx(false),
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      value={selectedGuests?.[0] ?? null}
                      onChange={(_, newValue) => {
                        setSelectedGuests(newValue ? [newValue] : []);
                        setValidationErrors((p) => ({ ...p, guest: undefined }));
                      }}
                      inputValue={bookingGuestSearchInput}
                      onInputChange={(_, value) => setBookingGuestSearchInput(value)}
                      options={bookingGuestOptions}
                      filterOptions={(options) => options}
                      getOptionLabel={(option) => (option && option.name) || ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      loading={bookingGuestOptionsLoading}
                      disabled={!isEditable}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              Assign guest
                              <InfoTooltip title="Primary guest. Search by name, mobile number, or email." isDark={isDark} />
                            </Box>
                          }
                          placeholder="Search by name or mobile..."
                          error={!!validationErrors.guest}
                          helperText={validationErrors.guest}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {bookingGuestOptionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={walkinFieldSx(!!validationErrors.guest)}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                            {(option.phone && option.phone !== '—') && (
                              <Typography variant="caption" color="text.secondary">{option.phone}</Typography>
                            )}
                            {(option.email && option.email !== '—') && (
                              <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                </Grid>
                {/* Special Requests and Internal Notes — after confirm booking status, same row on left of Payment Summary */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Special Requests
                          <InfoTooltip title="Guest preferences (e.g. early check-in, high floor). Visible to staff." isDark={isDark} />
                        </Box>
                      }
                      value={formData.special_requests}
                      onChange={(e) => handleFormChange('special_requests', e.target.value)}
                      disabled={!isEditable}
                      fullWidth
                      multiline
                      rows={2}
                      sx={walkinFieldSx(false)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          Internal Notes
                          <InfoTooltip title="Private notes for staff only. Not shared with the guest." isDark={isDark} />
                        </Box>
                      }
                      value={formData.internal_notes}
                      onChange={(e) => handleFormChange('internal_notes', e.target.value)}
                      disabled={!isEditable}
                      fullWidth
                      multiline
                      rows={2}
                      sx={walkinFieldSx(false)}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={savingSection === 'booking' ? <CircularProgress size={16} color="inherit" /> : <Save />}
                    onClick={() => setSaveConfirmDialog({ open: true, section: 'booking' })}
                    disabled={savingSection || (!isEditable && !canReenableViaStatus)}
                    sx={{
                      minHeight: 44,
                      py: 1.5,
                      backgroundColor: EDIT_BOOKING_ACCENT,
                      color: '#000',
                      '&:hover': { backgroundColor: EDIT_BOOKING_ACCENT, opacity: 0.95 },
                      '&:disabled': { backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0', color: isDark ? '#666' : '#999' },
                    }}
                  >
                    {savingSection === 'booking' ? 'Saving...' : 'Save Booking Information'}
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Payment Summary (same row as booking block) */}
            <Grid item xs={12} lg={4}>
              <Card elevation={0} sx={{ ...getEditBookingPaymentCardSx(isDark), p: 0, top: 20 }}>
                <Box sx={{ ...sectionHeaderStyle, p: 2, borderRadius: '8px 8px 0 0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(76, 225, 182, 0.15)', width: 36, height: 36 }}>
                      <Payment sx={{ color: EDIT_BOOKING_ACCENT, fontSize: 20 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? '#fff' : '#333' }}>
                      Payment Summary
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Box sx={{ mb: 2, p: 2, backgroundColor: isDark ? alpha('#ffffff', 0.04) : '#f9f9f9', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#fff' : '#333', mb: 1.5 }}>
                      Amount Calculation
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666' }}>Subtotal (before discount)</Typography>
                      <Typography variant="caption" sx={{ color: isDark ? surface.text : '#333' }}>₹{parseFloat(calculationBreakdown.subtotalBeforeDiscount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                    {(formData.discount_percentage > 0 || formData.discount_amount > 0) && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666' }}>
                            Discount{formData.discount_mode === 'percentage' && formData.discount_percentage > 0 ? ` (${formData.discount_percentage}%)` : formData.discount_mode === 'amount' && formData.discount_amount > 0 ? ` (₹${parseFloat(formData.discount_amount).toFixed(2)})` : ''}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#f44336' }}>- ₹{parseFloat(calculationBreakdown.discountAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666' }}>
                            {calculationBreakdown.afterDiscountLabel || 'After discount'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: isDark ? surface.text : '#333' }}>₹{parseFloat(calculationBreakdown.subtotalAfterDiscount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                        </Box>
                      </>
                    )}
                    {formData.gst_included && formData.gst_percentage > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666' }}>
                          {calculationBreakdown.taxInclusive ? '+ ' : ''}GST ({formData.gst_percentage}%)
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDark ? surface.text : '#333' }}>+ ₹{parseFloat(calculationBreakdown.gstAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                      </Box>
                    )}
                    {(paymentSummary?.extraChargesAmount || 0) > 0 && (
                      <BookingExtraChargesBreakdown
                        items={paymentSummary.extraChargesItems}
                        amount={paymentSummary.extraChargesAmount}
                        baseAmount={paymentSummary.extraChargesBaseAmount}
                        gstAmount={paymentSummary.extraChargesGstAmount}
                        note={paymentSummary.extraChargesNote}
                        currency={paymentSummary.currency || booking?.currency || 'INR'}
                        isDark={isDark}
                        accent={EDIT_BOOKING_ACCENT}
                        compact
                      />
                    )}
                    <Divider sx={{ my: 1, borderColor: isDark ? '#444' : '#e0e0e0' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#fff' : '#333' }}>Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: EDIT_BOOKING_ACCENT }}>₹{parseFloat(paymentDisplayTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: isDark ? surface.subtleText : '#666' }}>Total Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#fff' : '#333' }}>
                        ₹{parseFloat(paymentDisplayTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: isDark ? surface.subtleText : '#666' }}>Advance Paid</Typography>
                      <Typography variant="body2" sx={{ color: EDIT_BOOKING_ACCENT }}>
                        ₹{((formData.advance_amount ?? booking?.advance_amount) || 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: isDark ? surface.subtleText : '#666' }}>Fully Paid</Typography>
                      <Chip
                        label={formData.is_paid ? 'Yes' : 'No'}
                        size="small"
                        sx={{
                          backgroundColor: formData.is_paid ? 'rgba(76, 175, 80, 0.2)' : 'rgba(158, 158, 158, 0.2)',
                          color: formData.is_paid ? '#4caf50' : (isDark ? '#999' : '#666'),
                        }}
                      />
                    </Box>
                    {booking?.payment_summary?.total_paid > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: isDark ? surface.subtleText : '#666' }}>Total Paid</Typography>
                        <Typography variant="body2" sx={{ color: '#4caf50' }}>₹{parseFloat(booking?.payment_summary?.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 1.5, borderColor: isDark ? '#444' : '#e0e0e0' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? '#fff' : '#333' }}>Balance Due</Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          color: balanceDue > 0 ? '#f44336' : EDIT_BOOKING_ACCENT,
                        }}
                      >
                        ₹{balanceDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 2, borderColor: isDark ? '#444' : '#e0e0e0' }} />
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#fff' : '#333' }}>Payment Records</Typography>
                      {isEditable && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => {
                            setNewPaymentAmount(balanceDue > 0 ? balanceDue.toFixed(2) : '');
                            setNewPaymentMethod('CASH');
                            setNewPaymentReference('');
                            setNewPaymentDate(moment());
                            setAddPaymentDrawerOpen(true);
                          }}
                          sx={{
                            color: EDIT_BOOKING_ACCENT,
                            borderColor: EDIT_BOOKING_ACCENT,
                            '&:hover': { borderColor: EDIT_BOOKING_ACCENT, backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)' },
                          }}
                        >
                          Add Payment
                        </Button>
                      )}
                    </Box>
                    {booking?.payments && booking.payments.length > 0 && (
                      <>
                        {booking.payments.map((payment, index) => (
                          <Box
                            key={payment.id || index}
                            sx={{
                              p: 1.5,
                              mb: 1,
                              backgroundColor: isDark ? alpha('#ffffff', 0.04) : '#f5f5f5',
                              borderRadius: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: isDark ? '#fff' : '#333',
                                }}
                              >
                                ₹{(payment.amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </Typography>
                              <Typography variant="caption" sx={{ color: isDark ? '#888' : '#999' }}>
                                {payment.payment_method?.replace(/_/g, ' ')} • {payment.processed_at ? formatPaymentTime(payment.processed_at, booking?.hotel_timezone) : 'N/A'}
                                {payment.notes && ' • '}
                                {payment.notes}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isEditable && (
                                <Tooltip title="Delete payment">
                                  <IconButton size="small" onClick={() => setDeleteConfirmDialog({ open: true, payment })} sx={{ color: isDark ? '#f44336' : '#d32f2f', p: 0.5 }}>
                                    <Delete sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Chip
                                label={payment.status || 'Success'}
                                size="small"
                                sx={{
                                  backgroundColor: payment.status === 'success' || !payment.status ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                  color: payment.status === 'success' || !payment.status ? '#4caf50' : '#f44336',
                                  fontSize: '0.65rem',
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Room Details — after Payment Summary, full width */}
            <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    backgroundColor: isDark ? alpha('#ffffff', 0.04) : '#fff',
                    border: `1px solid ${surface.surfaceBorder}`,
                    borderRadius: 2,
                    boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.2)' : '0 2px 12px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderBottom: `1px solid ${surface.surfaceBorder}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Hotel sx={{ color: EDIT_BOOKING_ACCENT, fontSize: { xs: 20, sm: 22 } }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? '#fff' : '#333', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      Room Details
                    </Typography>
                    <Chip
                      label={`${groupedBookingItems.length} room${groupedBookingItems.length !== 1 ? 's' : ''} · ${bookingItems.length} night${bookingItems.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                        color: EDIT_BOOKING_ACCENT,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    />
                    <Box sx={{ ml: 'auto' }}>
                      <Tooltip title={addRoomTooltipTitle} arrow>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={savingSection === 'rooms' ? <CircularProgress size={14} color="inherit" /> : <Add sx={{ fontSize: 18 }} />}
                            onClick={handleOpenAddRoomDrawer}
                            disabled={savingSection === 'rooms' || !isEditable || !canAddRoomBeforeOrOnCheckIn}
                            sx={{
                              borderColor: EDIT_BOOKING_ACCENT,
                              color: EDIT_BOOKING_ACCENT,
                              '&:hover': {
                                borderColor: EDIT_BOOKING_ACCENT,
                                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.08)',
                              },
                            }}
                          >
                            Add Room
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    {(() => {
                      const tableCellBorder = surface.surfaceBorder;
                      const textPrimary = isDark ? surface.text : '#333';
                      const textSecondary = isDark ? '#aaa' : '#666';
                      const normalizeBookingStatus = (status) =>
                        String(status || '').toLowerCase().replace(/[\s_-]+/g, '');
                      // Room actions reflect saved booking status — not unsaved dropdown changes.
                      const savedBookingStatusNorm = normalizeBookingStatus(booking?.status);
                      const isPending = savedBookingStatusNorm === 'pending';
                      const isConfirmed = savedBookingStatusNorm === 'confirmed';
                      const isPreArrivalEditable = isPending || isConfirmed;
                      const isCheckedIn =
                        savedBookingStatusNorm === 'checkedin' ||
                        savedBookingStatusNorm === 'partialcheckin' ||
                        savedBookingStatusNorm === 'partialcheckout';
                      const isFullyCheckedOut = savedBookingStatusNorm === 'checkedout';
                      // Edit covers adults, children, daily rates, guests. Pre-arrival
                      // bookings should remain editable before check-in.
                      const showEditItemsAction = isPreArrivalEditable || isCheckedIn || isFullyCheckedOut;
                      const actionChipSx = {
                        height: 28,
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        borderRadius: '999px',
                        borderColor: EDIT_BOOKING_ACCENT,
                        color: EDIT_BOOKING_ACCENT,
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.06)',
                        cursor: 'pointer',
                        '& .MuiChip-icon': { color: EDIT_BOOKING_ACCENT, fontSize: 16, ml: 0.75 },
                        '& .MuiChip-label': { px: 0.85 },
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.16)' : 'rgba(76, 225, 182, 0.12)',
                          borderColor: EDIT_BOOKING_ACCENT,
                        },
                        '&.Mui-disabled': {
                          opacity: 0.45,
                          borderColor: isDark ? '#555' : '#ccc',
                          color: isDark ? '#888' : '#999',
                          '& .MuiChip-icon': { color: isDark ? '#888' : '#999' },
                        },
                      };
                      const removeActionChipSx = {
                        ...actionChipSx,
                        borderColor: isDark ? '#ef9a9a' : '#e57373',
                        color: isDark ? '#ef9a9a' : '#c62828',
                        backgroundColor: isDark ? 'rgba(239, 154, 154, 0.08)' : 'rgba(198, 40, 40, 0.05)',
                        '& .MuiChip-icon': { color: isDark ? '#ef9a9a' : '#c62828', fontSize: 16, ml: 0.75 },
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(239, 154, 154, 0.16)' : 'rgba(198, 40, 40, 0.1)',
                          borderColor: isDark ? '#ef9a9a' : '#e57373',
                        },
                      };
                      const isCheckedOutItem = (it) => {
                        const raw = it?.checkout;
                        if (typeof raw === 'string') {
                          return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
                        }
                        return raw === true || raw === 1;
                      };
                      const effectiveCheckInDate = formData.check_in_date && formData.check_in_date.isValid?.()
                        ? formData.check_in_date
                        : (booking?.check_in_date ? moment(booking.check_in_date) : null);
                      const canShowChangeRoom = !!(
                        isCheckedIn &&
                        effectiveCheckInDate &&
                        effectiveCheckInDate.isValid?.() &&
                        moment().startOf('day').isSameOrAfter(
                          effectiveCheckInDate.clone().startOf('day').add(1, 'day')
                        )
                      );
                      const showAllocateAction = isConfirmed || (isCheckedIn && !canShowChangeRoom);
                      const showChangeRoomAction = isCheckedIn && canShowChangeRoom;
                      // Confirmed guests can upgrade room type / meal plan before arrival;
                      // checked-in guests can upgrade mid-stay.
                      const showUpgradeAction = isConfirmed || isCheckedIn;
                      const showRoomActions = isPreArrivalEditable || isCheckedIn || showEditItemsAction;
                      const resolveRoomLabelForItem = (it, roomsForType = []) => {
                        const roomFromItem = it?.allocated_room_number;
                        const roomFromTypeList = !roomFromItem && it?.allocated_room_id
                          ? roomsForType.find((r) => r.id === it.allocated_room_id)
                          : null;
                        return roomFromItem
                          ? String(roomFromItem)
                          : roomFromTypeList?.room_number
                            ? String(roomFromTypeList.room_number)
                            : 'Unassigned';
                      };
                      const displayGroupedBookingItems = (() => {
                        const safeNumber = (value, fallback = 0) => {
                          const n = Number(value);
                          return Number.isFinite(n) ? n : fallback;
                        };
                        const normalizeName = (value) =>
                          String(value || '')
                            .normalize('NFKD')
                            .replace(/\s+/g, ' ')
                            .trim()
                            .toLowerCase();
                        const roomTypeNameById = new Map();
                        (roomTypes || []).forEach((rt) => {
                          const typeId = safeNumber(rt?.id, 0);
                          const typeName = normalizeName(rt?.name);
                          if (typeId && typeName && !roomTypeNameById.has(typeId)) {
                            roomTypeNameById.set(typeId, typeName);
                          }
                        });
                        groupedBookingItems.forEach((g) => {
                          (g?.items || []).forEach((it) => {
                            const typeId = safeNumber(it?.room_type_id, 0);
                            const typeName = normalizeName(it?.room_type_name ?? it?.room_type?.name);
                            if (typeId && typeName && !roomTypeNameById.has(typeId)) {
                              roomTypeNameById.set(typeId, typeName);
                            }
                          });
                        });
                        const getTypeTokens = (items = []) => {
                          const tokens = new Set();
                          items.forEach((it) => {
                            const typeId = safeNumber(it?.room_type_id, 0);
                            const typeName = normalizeName(it?.room_type_name ?? it?.room_type?.name);
                            if (typeId) tokens.add(`id_${typeId}`);
                            if (typeName) tokens.add(`name_${typeName}`);
                          });
                          return tokens;
                        };
                        const getSourceTypeTokens = (it) => {
                          const tokens = [];
                          const sourceId = safeNumber(
                            it?.upgraded_from_room_type_id ?? it?.upgraded_from_room_type?.id,
                            0
                          );
                          if (sourceId) {
                            tokens.push(`id_${sourceId}`);
                            const sourceTypeName = roomTypeNameById.get(sourceId);
                            if (sourceTypeName) tokens.push(`name_${sourceTypeName}`);
                          }
                          const sourceName = normalizeName(it?.upgraded_from_room_type?.name);
                          if (sourceName) tokens.push(`name_${sourceName}`);
                          return tokens;
                        };
                        const getDateRange = (items = []) => {
                          const dates = items
                            .map((it) =>
                              it?.date
                                ? (moment.isMoment(it.date) ? it.date.format('YYYY-MM-DD') : String(it.date).slice(0, 10))
                                : ''
                            )
                            .filter(Boolean)
                            .sort();
                          if (!dates.length) return null;
                          return { start: dates[0], end: dates[dates.length - 1] };
                        };
                        const hasUpgradeMarker = (items = []) =>
                          items.some((it) => getSourceTypeTokens(it).length > 0);
                        const areRangesOverlapping = (aRange, bRange) => {
                          if (!aRange || !bRange) return true;
                          return !(aRange.end < bRange.start || bRange.end < aRange.start);
                        };
                        const hasUpgradeLink = (a, b) => {
                          const aTypeTokens = getTypeTokens(a?.items || []);
                          const bTypeTokens = getTypeTokens(b?.items || []);
                          const aRange = getDateRange(a?.items || []);
                          const bRange = getDateRange(b?.items || []);
                          const rangesOverlap = areRangesOverlapping(aRange, bRange);
                          const aLinkedToB = (a?.items || []).some((it) => {
                            const sourceTokens = getSourceTypeTokens(it);
                            return sourceTokens.some((token) => bTypeTokens.has(token));
                          });
                          if (aLinkedToB && !rangesOverlap) return true;
                          const bLinkedToA = (b?.items || []).some((it) => {
                            const sourceTokens = getSourceTypeTokens(it);
                            return sourceTokens.some((token) => aTypeTokens.has(token));
                          });
                          if (bLinkedToA && !rangesOverlap) return true;

                          // Fallback: same sequence + explicit upgrade marker + non-overlapping date ranges.
                          // This handles payloads where upgraded_from_room_type_id is present but
                          // does not directly map to available room-type tokens in the paired group.
                          if (safeNumber(a?.room_sequence, 1) !== safeNumber(b?.room_sequence, 1)) return false;
                          const hasMarker = hasUpgradeMarker(a?.items || []) || hasUpgradeMarker(b?.items || []);
                          if (!hasMarker) return false;
                          if (rangesOverlap) return false;
                          return true;
                        };

                        // Merge row groups that belong to the same upgrade stream
                        // so booking edit shows one row with an upgrade indicator.
                        const mergedSeedGroups = [];
                        const visited = new Set();
                        groupedBookingItems.forEach((seedGroup, seedIndex) => {
                          if (visited.has(seedIndex)) return;
                          const stack = [seedIndex];
                          visited.add(seedIndex);
                          const clusterIndexes = [];
                          while (stack.length > 0) {
                            const currentIndex = stack.pop();
                            clusterIndexes.push(currentIndex);
                            const currentGroup = groupedBookingItems[currentIndex];
                            groupedBookingItems.forEach((candidateGroup, candidateIndex) => {
                              if (visited.has(candidateIndex)) return;
                              if (hasUpgradeLink(currentGroup, candidateGroup)) {
                                visited.add(candidateIndex);
                                stack.push(candidateIndex);
                              }
                            });
                          }

                          const clusterGroups = clusterIndexes.map((idx) => groupedBookingItems[idx]);
                          const itemsById = new Map();
                          clusterGroups.forEach((g) => {
                            (g?.items || []).forEach((it) => {
                              const idKey = it?.id ?? `${it?.room_type_id || 'rt'}_${it?.date || ''}_${it?.index || ''}`;
                              if (!itemsById.has(idKey)) itemsById.set(idKey, it);
                            });
                          });
                          const mergedItems = [...itemsById.values()].sort((a, b) => {
                            const aDate = String(a?.date || '');
                            const bDate = String(b?.date || '');
                            if (aDate !== bDate) return aDate.localeCompare(bDate);
                            return (a?.id || 0) - (b?.id || 0);
                          });
                          const roomTypeProgression = [];
                          const seenRoomTypeKeys = new Set();
                          mergedItems.forEach((it) => {
                            const typeId = safeNumber(it?.room_type_id, 0);
                            const typeName = String(it?.room_type_name || '').trim() || '—';
                            const typeKey = typeId ? `id_${typeId}` : `name_${typeName}`;
                            if (!seenRoomTypeKeys.has(typeKey)) {
                              seenRoomTypeKeys.add(typeKey);
                              roomTypeProgression.push(typeName);
                            }
                          });
                          const activeItems = mergedItems.filter((it) => it?.checkout !== true);
                          const preferredTypeItem =
                            activeItems[activeItems.length - 1] || mergedItems[mergedItems.length - 1] || null;
                          const hasUpgrade =
                            clusterGroups.length > 1 ||
                            mergedItems.some((it) => getSourceTypeTokens(it).length > 0);
                          const seed = clusterGroups[0] || seedGroup;
                          mergedSeedGroups.push({
                            ...seed,
                            key: clusterGroups.map((g) => g.key).join('__upgrade_merge__'),
                            room_type_name: preferredTypeItem?.room_type_name || seed?.room_type_name || '—',
                            room_type_display_name:
                              roomTypeProgression.length > 1
                                ? roomTypeProgression.join(' → ')
                                : (preferredTypeItem?.room_type_name || seed?.room_type_name || '—'),
                            items: mergedItems,
                            has_upgrade: hasUpgrade,
                          });
                        });

                        return mergedSeedGroups.flatMap((group) => {
                        const firstGroupItem = group.items?.[0];
                        const roomsForType = availableRooms[firstGroupItem?.room_type_id] || [];
                        const roomsByDate = new Map();
                        (group.items || []).forEach((it) => {
                          const dateKey = it?.date
                            ? (moment.isMoment(it.date) ? it.date.format('YYYY-MM-DD') : String(it.date).slice(0, 10))
                            : '';
                          const roomLabel = resolveRoomLabelForItem(it, roomsForType);
                          if (!roomsByDate.has(dateKey)) roomsByDate.set(dateKey, new Set());
                          roomsByDate.get(dateKey).add(roomLabel);
                        });
                        const hasMultipleRoomsOnSameDate = [...roomsByDate.values()].some((labels) => labels.size > 1);
                        if (!hasMultipleRoomsOnSameDate) {
                          const groupItemIds = (group.items || [])
                            .map((it) => Number(it?.id || 0))
                            .filter(Boolean);
                          return [{
                            ...group,
                            display_key: group.key,
                            delete_scope_item_ids: group.has_upgrade ? groupItemIds : groupItemIds,
                          }];
                        }

                        // Split into separate display rows by room label when same-date multi-room exists.
                        const itemsByRoomLabel = new Map();
                        (group.items || []).forEach((it) => {
                          const roomLabel = resolveRoomLabelForItem(it, roomsForType);
                          if (!itemsByRoomLabel.has(roomLabel)) itemsByRoomLabel.set(roomLabel, []);
                          itemsByRoomLabel.get(roomLabel).push(it);
                        });
                        const splitRows = [...itemsByRoomLabel.entries()]
                          .map(([roomLabel, items], idx) => {
                            const sortedItems = [...items].sort((a, b) => {
                              const aDate = String(a?.date || '');
                              const bDate = String(b?.date || '');
                              if (aDate !== bDate) return aDate.localeCompare(bDate);
                              return (a?.id || 0) - (b?.id || 0);
                            });
                            return {
                              ...group,
                              key: `${group.key}__split_${idx}`,
                              display_key: `${group.key}__split_${roomLabel}`,
                              items: sortedItems,
                              delete_scope_item_ids: group.has_upgrade
                                ? (group.items || []).map((it) => Number(it?.id || 0)).filter(Boolean)
                                : sortedItems.map((it) => Number(it?.id || 0)).filter(Boolean),
                            };
                          })
                          .sort((a, b) => {
                            const aDate = String(a.items?.[0]?.date || '');
                            const bDate = String(b.items?.[0]?.date || '');
                            if (aDate !== bDate) return aDate.localeCompare(bDate);
                            return String(a.display_key || '').localeCompare(String(b.display_key || ''));
                          });
                        return splitRows;
                      });
                      })();
                      const showRemoveRoomAction = isEditable && displayGroupedBookingItems.length > 1;
                      const showAnyRowActions = showRoomActions || showRemoveRoomAction;
                      return (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                          <Table size="small" sx={{ minWidth: 560 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: isDark ? surface.subtleText : '#666', borderColor: tableCellBorder, py: 1, width: 48 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: isDark ? surface.subtleText : '#666', borderColor: tableCellBorder, py: 1 }}>Room Type</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: isDark ? surface.subtleText : '#666', borderColor: tableCellBorder, py: 1 }}>Allocated Room / Stay Date</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: isDark ? surface.subtleText : '#666', borderColor: tableCellBorder, py: 1 }}>Meal Plan</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: isDark ? surface.subtleText : '#666', borderColor: tableCellBorder, py: 1 }}>Guest(s)</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: isDark ? surface.subtleText : '#666', borderColor: tableCellBorder, py: 1 }}>Occupancy</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: isDark ? surface.subtleText : '#666', borderColor: tableCellBorder, py: 1 }}>Price</TableCell>
                                {showAnyRowActions && (
                                  <TableCell sx={{ fontWeight: 600, color: isDark ? surface.subtleText : '#666', borderColor: tableCellBorder, py: 1 }}>Actions</TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {displayGroupedBookingItems.map((group, listIndex) => {
                                const first = group.items[0];
                                const last = group.items[group.items.length - 1];
                                const activeGroupItems = (group.items || []).filter((it) => !isCheckedOutItem(it));
                                const hasActiveItems = activeGroupItems.length > 0;
                                const roomsForType = availableRooms[first?.room_type_id] || [];
                                const sortedItemsByDate = [...(group.items || [])]
                                  .sort((a, b) => String(a?.date || '').localeCompare(String(b?.date || '')));
                                const mealPlanFromCatalog = ratePlans.find(
                                  (rp) => Number(rp?.id) === Number(first?.rate_plan_id)
                                );
                                const mealPlan =
                                  first?.rate_plan_name ||
                                  first?.rate_plan?.name ||
                                  mealPlanFromCatalog?.name ||
                                  mealPlanFromCatalog?.code ||
                                  first?.rate_plan?.code ||
                                  (first?.rate_plan_id ? 'Rate plan' : 'None');
                                const guestNamesList = (() => {
                                  const seen = new Set();
                                  const names = [];
                                  group.items.forEach((it) => {
                                    (it.guests || []).forEach((g) => {
                                      const fullName = [g?.first_name, g?.middle_name, g?.last_name].filter(Boolean).join(' ').trim() || g?.name || 'Guest';
                                      const key = g?.id ?? fullName;
                                      if (key && !seen.has(key)) {
                                        seen.add(key);
                                        names.push(fullName);
                                      }
                                    });
                                  });
                                  return names.length ? names.join(', ') : '—';
                                })();
                                const occupancySource = activeGroupItems[0] || first;
                                const roomAdults = Math.max(0, Number(occupancySource?.adults ?? 0) || 0);
                                const roomChildren = Math.max(0, Number(occupancySource?.children ?? 0) || 0);
                                const getOrdinal = (d) => {
                                  if (d >= 11 && d <= 13) return 'th';
                                  switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
                                };
                                const buildStayDateLabel = (itemsForLabel) => {
                                  const stayDateGroups = [];
                                  if (!itemsForLabel?.length) return '—';
                                  let currentMonthYear = null;
                                  let currentParts = [];
                                  itemsForLabel.forEach((it) => {
                                    const m = moment(it.date);
                                    if (!m.isValid()) return;
                                    const monthYear = m.format('MMM YYYY');
                                    const part = m.date() + getOrdinal(m.date());
                                    if (monthYear !== currentMonthYear) {
                                      if (currentParts.length) stayDateGroups.push({ monthYear: currentMonthYear, parts: currentParts });
                                      currentMonthYear = monthYear;
                                      currentParts = [];
                                    }
                                    currentParts.push(part);
                                  });
                                  if (currentParts.length) stayDateGroups.push({ monthYear: currentMonthYear, parts: currentParts });
                                  const formatGroup = (g) => {
                                  const dayStr = g.parts.length === 1 ? g.parts[0] : g.parts.slice(0, -1).join(', ') + ' and ' + g.parts[g.parts.length - 1];
                                  return dayStr + ' ' + g.monthYear;
                                };
                                  return stayDateGroups.length === 0
                                    ? '—'
                                    : stayDateGroups.map(formatGroup).join(' and ');
                                };

                                const resolveItemStayStatus = (it) => {
                                  if (isCheckedOutItem(it)) return 'Checked out';
                                  const checkedIn = it?.checked_in === true || it?.checked_in === 1 || it?.checked_in === 'true';
                                  if (checkedIn) return 'Checked in';
                                  if (Number(it?.allocated_room_id || 0) > 0 || it?.allocated_room_number) {
                                    return 'Booked';
                                  }
                                  return 'Unassigned';
                                };
                                const roomStaySegments = [];
                                sortedItemsByDate.forEach((it) => {
                                  const roomFromItem = it?.allocated_room_number;
                                  const roomFromTypeList = !roomFromItem && it?.allocated_room_id
                                    ? roomsForType.find((r) => r.id === it.allocated_room_id)
                                    : null;
                                  const roomLabel = roomFromItem
                                    ? String(roomFromItem)
                                    : roomFromTypeList?.room_number
                                      ? String(roomFromTypeList.room_number)
                                      : 'Unassigned';
                                  const stayStatus = resolveItemStayStatus(it);
                                  const lastSegment = roomStaySegments[roomStaySegments.length - 1];
                                  if (!lastSegment || lastSegment.roomLabel !== roomLabel || lastSegment.stayStatus !== stayStatus) {
                                    roomStaySegments.push({ roomLabel, stayStatus, items: [it] });
                                  } else {
                                    lastSegment.items.push(it);
                                  }
                                });
                                const changedRoomLabels = [...new Set(
                                  roomStaySegments
                                    .map((seg) => String(seg?.roomLabel || '').trim())
                                    .filter((label) => label && label !== 'Unassigned')
                                )];
                                const hasRoomChange = changedRoomLabels.length > 1;
                                const totalPrice = group.items.reduce((sum, it) => sum + (parseFloat(it.price) || 0), 0);
                                return (
                                  <TableRow key={group.display_key || group.key} sx={{ '& td': { borderColor: tableCellBorder, py: 1.5 } }}>
                                    <TableCell sx={{ color: textSecondary, fontWeight: 600 }}>{listIndex + 1}</TableCell>
                                    <TableCell sx={{ color: textPrimary }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" sx={{ color: textPrimary }}>
                                          {group.room_type_display_name || group.room_type_name || '—'}
                                        </Typography>
                                        {group.has_upgrade && (
                                          <Chip
                                            label="Upgrade"
                                            size="small"
                                            sx={{
                                              backgroundColor: isDark ? 'rgba(184, 134, 11, 0.2)' : 'rgba(184, 134, 11, 0.12)',
                                              color: '#b8860b',
                                              fontWeight: 600,
                                              fontSize: '0.65rem',
                                              height: 20,
                                              border: `1px solid ${isDark ? 'rgba(184, 134, 11, 0.45)' : 'rgba(184, 134, 11, 0.35)'}`,
                                            }}
                                          />
                                        )}
                                        {hasRoomChange && (
                                          <Chip
                                            label="Change Room"
                                            size="small"
                                            sx={{
                                              backgroundColor: isDark ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.12)',
                                              color: isDark ? '#64b5f6' : '#1565c0',
                                              fontWeight: 600,
                                              fontSize: '0.65rem',
                                              height: 20,
                                              border: `1px solid ${isDark ? 'rgba(33, 150, 243, 0.45)' : 'rgba(33, 150, 243, 0.35)'}`,
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ color: textPrimary, minWidth: 0 }}>
                                      {roomStaySegments.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                                          {roomStaySegments.map((seg, segIndex) => (
                                            <React.Fragment key={`${group.key}-seg-${segIndex}`}>
                                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                  <Chip
                                                    size="small"
                                                    label={`${seg.roomLabel}`}
                                                    sx={{
                                                      height: 22,
                                                      fontSize: '0.72rem',
                                                      fontWeight: 600,
                                                      backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#f5f5f5',
                                                      color: isDark ? surface.text : '#333',
                                                      border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                                                      '& .MuiChip-label': { px: 1 },
                                                    }}
                                                  />
                                                  {seg.stayStatus && seg.stayStatus !== 'Unassigned' && (
                                                    <Chip
                                                      size="small"
                                                      label={seg.stayStatus}
                                                      sx={{
                                                        height: 20,
                                                        fontSize: '0.62rem',
                                                        fontWeight: 700,
                                                        backgroundColor:
                                                          seg.stayStatus === 'Checked in'
                                                            ? (isDark ? 'rgba(76, 225, 182, 0.18)' : 'rgba(0, 160, 133, 0.12)')
                                                            : seg.stayStatus === 'Checked out'
                                                              ? (isDark ? 'rgba(158, 158, 158, 0.2)' : 'rgba(158, 158, 158, 0.14)')
                                                              : (isDark ? 'rgba(255, 167, 38, 0.18)' : 'rgba(255, 152, 0, 0.12)'),
                                                        color:
                                                          seg.stayStatus === 'Checked in'
                                                            ? (isDark ? '#4ce1b6' : '#00897b')
                                                            : seg.stayStatus === 'Checked out'
                                                              ? (isDark ? '#bdbdbd' : '#616161')
                                                              : (isDark ? '#ffb74d' : '#ef6c00'),
                                                        border: `1px solid ${
                                                          seg.stayStatus === 'Checked in'
                                                            ? (isDark ? 'rgba(76, 225, 182, 0.45)' : 'rgba(0, 160, 133, 0.35)')
                                                            : seg.stayStatus === 'Checked out'
                                                              ? (isDark ? 'rgba(158, 158, 158, 0.4)' : 'rgba(158, 158, 158, 0.35)')
                                                              : (isDark ? 'rgba(255, 167, 38, 0.45)' : 'rgba(255, 152, 0, 0.35)')
                                                        }`,
                                                        '& .MuiChip-label': { px: 0.75 },
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: textSecondary,
                                                    display: 'block',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere',
                                                    lineHeight: 1.35,
                                                  }}
                                                >
                                                  {buildStayDateLabel(seg.items)}
                                                </Typography>
                                              </Box>
                                              {segIndex < roomStaySegments.length - 1 && (
                                                <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 600, ml: 0.25 }}>
                                                  →
                                                </Typography>
                                              )}
                                            </React.Fragment>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" sx={{ color: textPrimary }}>Unassigned</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ color: textPrimary }}>{mealPlan}</TableCell>
                                    <TableCell sx={{ color: textPrimary, maxWidth: { xs: 120, sm: 180 }, minWidth: 0 }}>
                                      <Typography variant="body2" sx={{ color: textPrimary, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{guestNamesList}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: textPrimary, whiteSpace: 'nowrap' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                          <Person sx={{ fontSize: 16, color: EDIT_BOOKING_ACCENT }} />
                                          <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 600, lineHeight: 1 }}>
                                            {roomAdults}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                          <ChildCare sx={{ fontSize: 16, color: EDIT_BOOKING_ACCENT }} />
                                          <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 600, lineHeight: 1 }}>
                                            {roomChildren}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ color: textPrimary }}>₹{totalPrice.toFixed(2)}</TableCell>
                                    {(showRoomActions || showRemoveRoomAction) ? (
                                      <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                                          {showEditItemsAction && (
                                            <Chip
                                              clickable
                                              size="small"
                                              variant="outlined"
                                              icon={<EditIcon />}
                                              label="Edit"
                                              onClick={() => {
                                                setEditItemsDrawerGroup({ ...group, items: group.items || [] });
                                                setEditItemsDrawerOpen(true);
                                              }}
                                              sx={actionChipSx}
                                            />
                                          )}
                                          {showAllocateAction && (
                                            <Chip
                                              clickable
                                              size="small"
                                              variant="outlined"
                                              icon={<MeetingRoom />}
                                              label="Allocate Room"
                                              onClick={() => setAllocationDrawerOpen(true)}
                                              disabled={!hasActiveItems}
                                              sx={actionChipSx}
                                            />
                                          )}
                                          {showChangeRoomAction && (
                                            <Chip
                                              clickable
                                              size="small"
                                              variant="outlined"
                                              icon={<SwapHoriz />}
                                              label="Change"
                                              onClick={() => {
                                                setChangeRoomDrawerGroup({ ...group, items: activeGroupItems });
                                                setChangeRoomDrawerOpen(true);
                                              }}
                                              disabled={!hasActiveItems}
                                              sx={actionChipSx}
                                            />
                                          )}
                                          {showUpgradeAction && (
                                            <Chip
                                              clickable
                                              size="small"
                                              variant="outlined"
                                              icon={<TrendingUp />}
                                              label="Upgrade"
                                              onClick={() => {
                                                setUpgradeDrawerGroup({ ...group, items: activeGroupItems });
                                                setUpgradeDrawerOpen(true);
                                              }}
                                              disabled={!hasActiveItems}
                                              sx={actionChipSx}
                                            />
                                          )}
                                          {showRemoveRoomAction && (
                                            <Chip
                                              clickable
                                              size="small"
                                              variant="outlined"
                                              icon={<Delete />}
                                              label="Remove"
                                              onClick={() => handleOpenRemoveRoomConfirm(group, displayGroupedBookingItems.length)}
                                              disabled={savingSection === 'rooms'}
                                              sx={removeActionChipSx}
                                            />
                                          )}
                                        </Box>
                                      </TableCell>
                                    ) : null}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      );
                    })()}
                  </Box>
                </Card>
            </Grid>

          </Grid>
        </Paper>

        {/* Add Payment Drawer */}
        <Drawer
          anchor="right"
          open={addPaymentDrawerOpen}
          onClose={() => {
            if (!addingPayment) {
              setAddPaymentDrawerOpen(false);
              setNewPaymentAmount('');
              setNewPaymentMethod('CASH');
              setNewPaymentReference('');
              setNewPaymentDate(moment());
              setPaymentReceipt(null);
              setPaymentCameraUpload(false);
            }
          }}
          sx={getDrawerRootSx()}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 600 },
              backgroundColor: surface.surface,
            },
          }}
        >
          <Box ref={addPaymentDrawerRootRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Header */}
            <Box sx={{ borderBottom: `2px solid ${surface.surfaceBorderStrong}`, backgroundColor: surface.surface }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
                <PaymentDrawerHeaderTitle
                  title="Add Payment"
                  booking={booking}
                  bookingId={bookingId}
                  paymentHistory={booking?.payments || []}
                  isDark={isDark}
                  drawerTheme={surface}
                  accentColor={EDIT_BOOKING_ACCENT}
                />
                <IconButton
                  onClick={() => {
                    if (!addingPayment) {
                      setAddPaymentDrawerOpen(false);
                      setNewPaymentDate(moment());
                      setPaymentReceipt(null);
                      setPaymentCameraUpload(false);
                    }
                  }}
                  sx={{ color: isDark ? surface.subtleText : '#666' }}
                >
                  <Close />
                </IconButton>
              </Box>
              {addingPayment && (
                <LinearProgress
                  sx={{
                    height: 3,
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    '& .MuiLinearProgress-bar': { backgroundColor: EDIT_BOOKING_ACCENT },
                  }}
                />
              )}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {(() => {
                return (
                  <>
                    {booking ? (
                      <BookingPaymentSummaryCard
                        summary={{
                          ...paymentSummary,
                          totalAmount: paymentDisplayTotal,
                          displayTotal: paymentDisplayTotal,
                          remainingAmount: balanceDue,
                          returnAmount: roundEditBookingAmount(
                            Math.max(0, (Number(paymentSummary?.paidAmount) || 0) - paymentDisplayTotal),
                          ),
                        }}
                        bookingId={normalizedBookingId}
                        isDark={isDark}
                        drawerTheme={surface}
                        overlayMountRef={addPaymentDrawerRootRef}
                        onExtraChargesSaved={async (result) => {
                          if (result?.booking) {
                            setBooking(result.booking);
                          } else {
                            await fetchBookingData();
                          }
                        }}
                      />
                    ) : null}

                    <BookingFinancialHistoryPanel
                      payments={booking?.payments || []}
                      currency={booking?.currency || 'INR'}
                      hotelTimezone={booking?.hotel_timezone}
                      isDark={isDark}
                      drawerTheme={surface}
                      accent={EDIT_BOOKING_ACCENT}
                    />

                    {/* Collect Payment Form */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        backgroundColor: surface.surface,
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        borderRadius: '12px',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: isDark ? surface.text : '#333', fontWeight: 600, mb: 1.5 }}>Collect Payment</Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Payment Amount"
                            type="number"
                            value={newPaymentAmount}
                            onChange={(e) => setNewPaymentAmount(e.target.value)}
                            inputProps={{ min: 0.01, step: 0.01 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            sx={{
                              ...walkinFieldSx(false),
                              '& input[type=number]': { MozAppearance: 'textfield' },
                              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                        <DateTimePicker
                          label="Payment Date & Time"
                          disableFuture
                          value={newPaymentDate}
                            onChange={setNewPaymentDate}
                            slotProps={{
                              ...drawerDatePickerSlotProps,
                              textField: {
                                fullWidth: true,
                                size: 'medium',
                                sx: walkinFieldSx(false),
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666', fontSize: '0.7rem', fontWeight: 600, mb: 1, display: 'block' }}>Payment Method *</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {paymentMethods.map((method) => (
                              <Chip
                                key={method}
                                label={method}
                                onClick={() => setNewPaymentMethod(method)}
                                sx={{
                                  cursor: 'pointer',
                                  backgroundColor: newPaymentMethod === method ? EDIT_BOOKING_ACCENT : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                                  color: newPaymentMethod === method ? '#000' : EDIT_BOOKING_ACCENT,
                                  border: `1px solid ${newPaymentMethod === method ? EDIT_BOOKING_ACCENT : (isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)')}`,
                                  fontWeight: newPaymentMethod === method ? 700 : 600,
                                  fontSize: '0.75rem',
                                  height: 32,
                                  '&:hover': { backgroundColor: newPaymentMethod === method ? EDIT_BOOKING_ACCENT : (isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)') },
                                }}
                              />
                            ))}
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Reference Number (Optional)"
                            value={newPaymentReference}
                            onChange={(e) => setNewPaymentReference(e.target.value)}
                            placeholder="Transaction ID / Reference"
                            sx={walkinFieldSx(false)}
                          />
                        </Grid>
                        {/* Payment Receipt Upload */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? surface.text : '#333' }}>
                                Payment Receipt (Optional)
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                                <FormControlLabel
                                  label="Camera upload"
                                  labelPlacement="start"
                                  control={
                                    <Switch
                                      checked={paymentCameraUpload}
                                      onChange={(e) => setPaymentCameraUpload(e.target.checked)}
                                      color="primary"
                                    />
                                  }
                                  sx={{ color: isDark ? surface.text : '#333' }}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                          <Paper
                            sx={{
                              p: 2,
                              border: `2px dashed ${EDIT_BOOKING_ACCENT}`,
                              borderRadius: 2,
                              textAlign: 'center',
                              cursor: 'pointer',
                              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                              minHeight: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover': {
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                            onClick={handlePaymentReceiptClick}
                          >
                            {paymentReceipt ? (
                              <Box>
                                <img
                                  src={paymentReceipt}
                                  alt="Payment Receipt"
                                  style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                                />
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPaymentReceipt(null);
                                  }}
                                  sx={{ mt: 1 }}
                                >
                                  Remove
                                </Button>
                              </Box>
                            ) : (
                              <Box>
                                {paymentCameraUpload ? (
                                  <CameraAlt sx={{ fontSize: 48, color: isDark ? surface.subtleText : '#666', mb: 1 }} />
                                ) : (
                                  <CloudUpload sx={{ fontSize: 48, color: isDark ? surface.subtleText : '#666', mb: 1 }} />
                                )}
                                <Typography variant="body2" sx={{ color: isDark ? surface.subtleText : '#666' }}>
                                  {paymentCameraUpload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                                </Typography>
                              </Box>
                            )}
                            <input
                              ref={paymentReceiptFileInputRef}
                              type="file"
                              accept="image/*"
                              style={{ display: 'none' }}
                              onChange={handlePaymentReceiptUpload}
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    </Paper>
                  </>
                );
              })()}
            </Box>

            {/* Footer */}
            <Box sx={{ p: 1.5, borderTop: `2px solid ${surface.surfaceBorderStrong}`, backgroundColor: surface.surface }}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      if (!addingPayment) {
                        setAddPaymentDrawerOpen(false);
                        setPaymentReceipt(null);
                        setPaymentCameraUpload(false);
                      }
                    }}
                    disabled={addingPayment}
                    sx={{
                      borderColor: surface.surfaceBorder,
                      color: isDark ? surface.text : '#666',
                      '&:hover': {
                        borderColor: EDIT_BOOKING_ACCENT,
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        color: EDIT_BOOKING_ACCENT,
                      },
                      '&:disabled': {
                        borderColor: surface.surfaceBorder,
                        color: isDark ? '#666' : '#999',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    fullWidth
                disabled={addingPayment || !newPaymentAmount || parseFloat(newPaymentAmount) <= 0 || !newPaymentMethod}
                onClick={async () => {
                  if (!bookingId || !newPaymentAmount || parseFloat(newPaymentAmount) <= 0) return;
                  setAddingPayment(true);
                  try {
                    const amount = parseFloat(newPaymentAmount);
                    const processedAtIso = toPaymentProcessedAtIso(newPaymentDate);
                    if (paymentReceipt) {
                      const formData = new FormData();
                      formData.append('booking', bookingId);
                      formData.append('amount', amount);
                      formData.append('payment_type', 'BALANCE');
                      formData.append('payment_method', newPaymentMethod);
                      if (newPaymentReference) formData.append('reference_number', newPaymentReference);
                      formData.append('status', 'SUCCESS');
                      if (processedAtIso) formData.append('processed_at', processedAtIso);
                      if (paymentReceipt.startsWith('data:image')) {
                        const blobResponse = await fetch(paymentReceipt);
                        const blob = await blobResponse.blob();
                        formData.append('document', blob, 'payment_receipt.jpg');
                      } else if (paymentReceipt instanceof File) {
                        formData.append('document', paymentReceipt);
                      }
                      await api.post('/finance/api/payments/', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                    } else {
                      await updateBooking(bookingId, {
                        new_payment: {
                          amount,
                          payment_method: newPaymentMethod,
                          payment_type: 'BALANCE',
                          reference_number: newPaymentReference || undefined,
                          processed_at: processedAtIso,
                        },
                      });
                    }
                    setSuccessMessage('Payment added successfully.');
                    setAddPaymentDrawerOpen(false);
                    setNewPaymentAmount('');
                    setNewPaymentMethod('CASH');
                    setNewPaymentReference('');
                    setNewPaymentDate(moment());
                    setPaymentReceipt(null);
                    setPaymentCameraUpload(false);
                    await fetchBookingData();
                  } catch (err) {
                    const errMsg = getErrorMessage(err, 'Failed to add payment');
                    setError(errMsg);
                    setErrorDialogOpen(true);
                  } finally {
                    setAddingPayment(false);
                  }
                }}
                sx={{
                  backgroundColor: EDIT_BOOKING_ACCENT,
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: EDIT_BOOKING_ACCENT, opacity: 0.9 },
                  '&:disabled': { backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0', color: isDark ? surface.subtleText : '#666' },
                }}
              >
                {addingPayment ? 'Adding...' : 'Collect Payment'}
              </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Drawer>

        {/* Add Room Drawer */}
        <Drawer
          anchor="right"
          open={addRoomDrawerOpen}
          onClose={() => {
            if (!addRoomSubmitting) {
              setAddRoomDrawerOpen(false);
              setAddRoomError('');
            }
          }}
          sx={getDrawerRootSx()}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 600 },
              backgroundColor: surface.surface,
            },
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                borderBottom: `2px solid ${surface.surfaceBorderStrong}`,
                backgroundColor: surface.surface,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Add sx={{ color: EDIT_BOOKING_ACCENT, fontSize: 24 }} />
                <Typography variant="h6" sx={{ color: isDark ? surface.text : '#333', fontWeight: 600 }}>
                  Add Room
                </Typography>
                {booking?.code && (
                  <>
                    <Typography variant="body2" sx={{ color: isDark ? surface.subtleText : '#666', mx: 0.5 }}>
                      -
                    </Typography>
                    <Chip
                      label={booking.code}
                      size="small"
                      sx={{
                        backgroundColor: EDIT_BOOKING_ACCENT,
                        color: '#000',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  </>
                )}
              </Box>
              <IconButton
                onClick={() => {
                  if (!addRoomSubmitting) {
                    setAddRoomDrawerOpen(false);
                    setAddRoomError('');
                  }
                }}
                size="small"
                sx={{ color: isDark ? surface.subtleText : '#666', p: 0.5 }}
              >
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 2,
                  backgroundColor: surface.surface,
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                  borderRadius: '12px',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: isDark ? surface.text : '#333', fontWeight: 600, mb: 0.5 }}>
                  Available Room Types
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? surface.subtleText : '#666' }}>
                  Select a room type to filter rooms. By default, all available room types are shown.
                </Typography>
                <FormControl fullWidth sx={{ mt: 1.5 }}>
                  <InputLabel>Room Type Filter</InputLabel>
                  <Select
                    value={addRoomFilterTypeId}
                    label="Room Type Filter"
                    MenuProps={drawerSelectMenuProps}
                    onChange={(e) => {
                      const nextFilter = e.target.value;
                      setAddRoomFilterTypeId(nextFilter);
                      if (nextFilter !== 'all' && String(addRoomSelectedTypeId || '') !== String(nextFilter)) {
                        setAddRoomSelectedTypeId('');
                        setAddRoomSelectedRoomId(null);
                      }
                      setAddRoomError('');
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: 56,
                      },
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '56px !important',
                        py: 0,
                      },
                    }}
                  >
                    <MenuItem value="all">
                      {`All Room Type (${addRoomTypeOptions.reduce((sum, opt) => sum + opt.availableCount, 0)})`}
                    </MenuItem>
                    {addRoomTypeOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {`${opt.name} (${opt.availableCount})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>

              {addRoomError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {addRoomError}
                </Alert>
              )}

              {!addRoomTypeOptions.length ? (
                <Alert severity="info">No available rooms found for this booking date range.</Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {addRoomDisplayGroups.map((group) => (
                    <Paper
                      key={group.id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        backgroundColor: surface.surface,
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        borderRadius: '12px',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                        <Typography variant="subtitle2" sx={{ color: isDark ? surface.text : '#333', fontWeight: 600 }}>
                          {group.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${group.availableCount} available`}
                          sx={{
                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                            color: EDIT_BOOKING_ACCENT,
                            fontWeight: 600,
                            fontSize: '0.72rem',
                          }}
                        />
                      </Box>
                      <Grid container spacing={2}>
                        {group.rooms.map((room) => {
                          const isSelected =
                            Number(addRoomSelectedRoomId || 0) === Number(room?.id || 0) &&
                            Number(addRoomSelectedTypeId || 0) === Number(group?.id || 0);
                          return (
                            <Grid item xs={6} sm={4} md={3} key={`${group.id}-${room.id}`}>
                              <Card
                                elevation={1}
                                onClick={() => {
                                  const isTypeChanged = String(addRoomSelectedTypeId || '') !== String(group.id);
                                  setAddRoomSelectedTypeId(String(group.id));
                                  setAddRoomSelectedRoomId(room.id);
                                  if (isTypeChanged) {
                                    const baseAdults = Math.max(1, Number(group?.base_adults || 1));
                                    const baseChildren = Math.max(0, Number(group?.base_children || 0));
                                    const maxGuests = Math.max(1, Number(group?.max_guests || 4));
                                    const nextAdults = Math.min(baseAdults, maxGuests);
                                    const nextChildren = Math.min(baseChildren, Math.max(0, maxGuests - baseAdults));
                                    setAddRoomAdults(nextAdults);
                                    setAddRoomChildren(nextChildren);
                                    setAddRoomRatePerNight(
                                      computeAddRoomRatePerNight(group?.id, nextAdults, nextChildren, addRoomRatePerNight)
                                    );
                                  }
                                  setAddRoomError('');
                                }}
                                sx={{
                                  backgroundColor: surface.surface,
                                  borderRadius: 3,
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  border: `1px solid ${isSelected ? EDIT_BOOKING_ACCENT : 'transparent'}`,
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: isDark
                                      ? '0 8px 16px rgba(76, 225, 182, 0.3)'
                                      : '0 8px 16px rgba(76, 225, 182, 0.2)',
                                  },
                                }}
                              >
                                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                  <Box sx={{ position: 'relative', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box
                                      sx={{
                                        flex: 1,
                                        minHeight: 120,
                                        position: 'relative',
                                        backgroundImage: (group.image || group.image_url)
                                          ? `url(${group.image || group.image_url})`
                                          : `linear-gradient(135deg, ${isDark ? '#3a3a4a' : '#e8e8e8'} 0%, ${isDark ? '#2a2a3a' : '#d8d8d8'} 100%)`,
                                        backgroundColor: isDark ? '#3a3a4a' : '#e8e8e8',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {!(group.image || group.image_url) && (
                                        <RoomIcon sx={{ fontSize: 32, color: isDark ? '#666' : '#999', opacity: 0.5 }} />
                                      )}
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          height: '50%',
                                          background: `linear-gradient(180deg,
                                            ${isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.6)'} 0%,
                                            ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.4)'} 50%,
                                            transparent 100%)`,
                                          pointerEvents: 'none',
                                        }}
                                      />
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          left: 8,
                                          right: 8,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          zIndex: 2,
                                        }}
                                      >
                                        <Checkbox
                                          checked={isSelected}
                                          size="small"
                                          sx={{
                                            color: '#fff',
                                            backgroundColor: isSelected ? 'rgba(76, 225, 182, 0.9)' : 'rgba(0, 0, 0, 0.4)',
                                            borderRadius: '4px',
                                            padding: '2px',
                                            backdropFilter: 'blur(8px)',
                                            '&.Mui-checked': {
                                              color: '#000',
                                              backgroundColor: 'rgba(76, 225, 182, 0.9)',
                                            },
                                            '& .MuiSvgIcon-root': {
                                              fontSize: 20,
                                            },
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const isTypeChanged = String(addRoomSelectedTypeId || '') !== String(group.id);
                                            setAddRoomSelectedTypeId(String(group.id));
                                            setAddRoomSelectedRoomId(room.id);
                                            if (isTypeChanged) {
                                              const baseAdults = Math.max(1, Number(group?.base_adults || 1));
                                              const baseChildren = Math.max(0, Number(group?.base_children || 0));
                                              const maxGuests = Math.max(1, Number(group?.max_guests || 4));
                                              const nextAdults = Math.min(baseAdults, maxGuests);
                                              const nextChildren = Math.min(baseChildren, Math.max(0, maxGuests - baseAdults));
                                              setAddRoomAdults(nextAdults);
                                              setAddRoomChildren(nextChildren);
                                              setAddRoomRatePerNight(
                                                computeAddRoomRatePerNight(group?.id, nextAdults, nextChildren, addRoomRatePerNight)
                                              );
                                            }
                                            setAddRoomError('');
                                          }}
                                        />
                                        <Chip
                                          icon={<RoomIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                          label={room?.room_number || `Room ${room?.id}`}
                                          size="small"
                                          sx={{
                                            backgroundColor: isSelected ? 'rgba(76, 225, 182, 0.9)' : 'rgba(0, 0, 0, 0.5)',
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: '0.85rem',
                                            height: 28,
                                            backdropFilter: 'blur(10px)',
                                            border: `1.5px solid ${isSelected ? 'rgba(76, 225, 182, 1)' : 'rgba(255, 255, 255, 0.3)'}`,
                                            letterSpacing: '0.5px',
                                            '& .MuiChip-icon': {
                                              color: '#fff !important',
                                              marginLeft: '8px',
                                            },
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        p: 1.25,
                                        backgroundColor: isDark ? 'rgba(42, 42, 49, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        borderTop: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: isDark ? surface.text : '#333',
                                          fontSize: '0.75rem',
                                          fontWeight: 500,
                                          display: 'block',
                                          textAlign: 'center',
                                        }}
                                      >
                                        {group.name}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                      {String(addRoomSelectedTypeId || '') === String(group.id) && (
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 1.5,
                            p: 1.5,
                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.06)' : 'rgba(76, 225, 182, 0.04)',
                            border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.2)'}`,
                            borderRadius: '10px',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ color: isDark ? surface.text : '#333', fontWeight: 600 }}>
                              Guest & Rate Details
                            </Typography>
                            <Chip
                              size="small"
                              label={`Max Guests: ${addRoomMaxGuests}`}
                              sx={{
                                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.12)',
                                color: EDIT_BOOKING_ACCENT,
                                fontWeight: 600,
                                fontSize: '0.72rem',
                              }}
                            />
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: isDark ? surface.text : '#333' }}>
                                Adults (Max: {addRoomMaxAdults})
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleAddRoomGuestCountChange('adults', -1)}
                                  disabled={addRoomAdults <= 0}
                                  sx={{
                                    border: `1px solid ${EDIT_BOOKING_ACCENT}`,
                                    '&:hover': { border: `1px solid ${EDIT_BOOKING_ACCENT}`, backgroundColor: `${EDIT_BOOKING_ACCENT}20` },
                                    '&.Mui-disabled': { border: `1px solid ${surface.surfaceBorder}` },
                                  }}
                                >
                                  <Remove />
                                </IconButton>
                                <Typography variant="h6" sx={{ minWidth: 44, textAlign: 'center', color: isDark ? surface.text : '#333', fontWeight: 500 }}>
                                  {addRoomAdults || 0}
                                </Typography>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleAddRoomGuestCountChange('adults', 1)}
                                  disabled={addRoomAdults >= addRoomMaxAdults}
                                  sx={{
                                    border: `1px solid ${EDIT_BOOKING_ACCENT}`,
                                    '&:hover': { border: `1px solid ${EDIT_BOOKING_ACCENT}`, backgroundColor: `${EDIT_BOOKING_ACCENT}20` },
                                    '&.Mui-disabled': { border: `1px solid ${surface.surfaceBorder}` },
                                  }}
                                >
                                  <Add />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: isDark ? surface.text : '#333' }}>
                                Children (Max: {addRoomMaxChildren})
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleAddRoomGuestCountChange('children', -1)}
                                  disabled={addRoomChildren <= 0}
                                  sx={{
                                    border: `1px solid ${EDIT_BOOKING_ACCENT}`,
                                    '&:hover': { border: `1px solid ${EDIT_BOOKING_ACCENT}`, backgroundColor: `${EDIT_BOOKING_ACCENT}20` },
                                    '&.Mui-disabled': { border: `1px solid ${surface.surfaceBorder}` },
                                  }}
                                >
                                  <Remove />
                                </IconButton>
                                <Typography variant="h6" sx={{ minWidth: 44, textAlign: 'center', color: isDark ? surface.text : '#333', fontWeight: 500 }}>
                                  {addRoomChildren || 0}
                                </Typography>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleAddRoomGuestCountChange('children', 1)}
                                  disabled={addRoomChildren >= addRoomMaxChildren}
                                  sx={{
                                    border: `1px solid ${EDIT_BOOKING_ACCENT}`,
                                    '&:hover': { border: `1px solid ${EDIT_BOOKING_ACCENT}`, backgroundColor: `${EDIT_BOOKING_ACCENT}20` },
                                    '&.Mui-disabled': { border: `1px solid ${surface.surfaceBorder}` },
                                  }}
                                >
                                  <Add />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>

                          <TextField
                            fullWidth
                            label="Rate Per Night"
                            type="number"
                            value={addRoomRatePerNight}
                            onChange={(e) => {
                              const nextPrice = Math.max(0, parseFloat(e.target.value) || 0);
                              setAddRoomRatePerNight(nextPrice);
                              setAddRoomError('');
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: EDIT_BOOKING_ACCENT },
                                '&:hover fieldset': { borderColor: EDIT_BOOKING_ACCENT },
                                '&.Mui-focused fieldset': { borderColor: EDIT_BOOKING_ACCENT },
                              },
                            }}
                          />
                        </Paper>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 1.5,
                borderTop: `2px solid ${surface.surfaceBorderStrong}`,
                backgroundColor: surface.surface,
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      if (!addRoomSubmitting) {
                        setAddRoomDrawerOpen(false);
                        setAddRoomError('');
                      }
                    }}
                    disabled={addRoomSubmitting}
                    sx={{
                      borderColor: surface.surfaceBorder,
                      color: isDark ? surface.text : '#666',
                      '&:hover': {
                        borderColor: EDIT_BOOKING_ACCENT,
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        color: EDIT_BOOKING_ACCENT,
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleConfirmAddRoom}
                    disabled={addRoomSubmitting || !addRoomSelectedTypeId || !addRoomSelectedRoomId || !addRoomTypeOptions.length}
                    sx={{
                      backgroundColor: EDIT_BOOKING_ACCENT,
                      color: '#000',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: EDIT_BOOKING_ACCENT,
                        opacity: 0.9,
                        boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                      },
                      '&:disabled': {
                        backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                        color: isDark ? surface.subtleText : '#666',
                      },
                    }}
                  >
                    {addRoomSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Add Room'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Drawer>

        <RoomAllocationDrawer
          bookingId={normalizedBookingId}
          open={allocationDrawerOpen}
          onClose={() => setAllocationDrawerOpen(false)}
          onAssignSuccess={() => {
            setAllocationDrawerOpen(false);
            fetchBookingData();
          }}
        />

        <RoomAllocationDrawer
          mode="changeRoom"
          bookingId={normalizedBookingId}
          open={changeRoomDrawerOpen}
          onClose={() => { setChangeRoomDrawerOpen(false); setChangeRoomDrawerGroup(null); }}
          group={changeRoomDrawerGroup}
          booking={booking}
          onChangeRoomSuccess={async ({
            newRoomId,
            changeFromDate: fromStr,
            group: changeGroup,
            streamAssignments,
          }) => {
            const effectiveFromDate = fromStr || moment().format('YYYY-MM-DD');
            const changedAtIso = moment().toISOString();
            let nextItems = [...bookingItems];
            const clickedGroupItemIds = new Set((changeGroup?.items || []).map((i) => i.id).filter(Boolean));
            const normalizedAssignments = Array.isArray(streamAssignments)
              ? streamAssignments.filter(Boolean)
              : [];

            if (normalizedAssignments.length > 0) {
              normalizedAssignments.forEach((assignment) => {
                const assignmentTypeId = Number(assignment?.targetRoomTypeId ?? 0) || null;
                const assignmentSequence = Number(assignment?.targetSequence ?? 1) || 1;
                const assignmentRoomId = assignment?.newRoomId ?? null;
                if (!assignmentTypeId) return;

                nextItems = nextItems.map((item) => {
                  if (clickedGroupItemIds.size > 0 && !clickedGroupItemIds.has(item.id)) return item;
                  const itemTypeId = item?.room_type_id ?? item?.room_type?.id ?? null;
                  const itemSeq = item?.room_sequence ?? 1;
                  const isTargetStream =
                    String(itemTypeId) === String(assignmentTypeId) &&
                    Number(itemSeq) === Number(assignmentSequence);
                  if (!isTargetStream) return item;
                  if (item?.checkout === true) return item;

                  const streamItems = nextItems.filter((candidate) => {
                    const candidateTypeId = candidate?.room_type_id ?? candidate?.room_type?.id ?? null;
                    const candidateSeq = candidate?.room_sequence ?? 1;
                    return (
                      String(candidateTypeId) === String(assignmentTypeId)
                      && Number(candidateSeq) === Number(assignmentSequence)
                    );
                  });
                  if (shouldCheckoutOldRoomSegment(item, effectiveFromDate, streamItems)) {
                    return {
                      ...item,
                      checkout: true,
                      segment_ended_at: changedAtIso,
                    };
                  }
                  return {
                    ...item,
                    allocated_room_id: assignmentRoomId,
                    checkout: false,
                    segment_started_at: changedAtIso,
                    room_changed_at: changedAtIso,
                  };
                });
              });
            } else {
              // Backward-compatible fallback path.
              if (!changeGroup?.items?.length) return;
              const groupItemIds = new Set(changeGroup.items.map((i) => i.id).filter(Boolean));
              nextItems = nextItems.map((item) => {
                if (!groupItemIds.has(item.id)) return item;
                if (item?.checkout === true) return item;
                if (shouldCheckoutOldRoomSegment(item, effectiveFromDate, changeGroup.items)) {
                  return {
                    ...item,
                    checkout: true,
                    segment_ended_at: changedAtIso,
                  };
                }
                return {
                  ...item,
                  allocated_room_id: newRoomId,
                  checkout: false,
                  segment_started_at: changedAtIso,
                  room_changed_at: changedAtIso,
                };
              });
            }
            setBookingItems(nextItems);
            const updateData = buildRoomsUpdatePayload(nextItems);
            await updateBooking(bookingId, updateData);
            setSuccessMessage('Room changed successfully.');
            await fetchBookingData();
          }}
        />

        <EditBookingItemsDrawer
          open={editItemsDrawerOpen}
          onClose={() => { setEditItemsDrawerOpen(false); setEditItemsDrawerGroup(null); }}
          group={editItemsDrawerGroup}
          booking={booking}
          bookingId={bookingId}
          roomTypes={roomTypes}
          ratePlans={ratePlans}
          onGuestsChanged={async () => {
            const refreshed = await fetchBookingData();
            // Keep open drawer group in sync with latest guests after add/edit/remove.
            setEditItemsDrawerGroup((prev) => {
              if (!prev?.items?.length) return prev;
              const idSet = new Set(
                (prev.items || []).map((it) => Number(it?.id)).filter(Boolean),
              );
              const sourceItems = Array.isArray(refreshed?.items)
                ? refreshed.items
                : (Array.isArray(refreshed?.booking_items) ? refreshed.booking_items : null);
              if (!sourceItems) return prev;
              const nextItems = sourceItems.filter((it) => idSet.has(Number(it?.id)));
              if (!nextItems.length) return prev;
              return { ...prev, items: nextItems };
            });
          }}
          onSave={async ({ rateUpdates, adults, children, itemIds } = {}) => {
            const priceById = new Map((rateUpdates || []).map((u) => [u.id, u.price]));
            const checkedInById = new Map((rateUpdates || []).map((u) => [u.id, u.checked_in]));
            const checkoutById = new Map((rateUpdates || []).map((u) => [u.id, u.checkout]));
            const editIdSet = new Set((itemIds || []).map((id) => Number(id)));
            const nextItems = bookingItems.map((item) => {
              let next = item;
              const newPrice = item.id != null ? priceById.get(item.id) : undefined;
              if (newPrice !== undefined) {
                next = { ...next, price: newPrice };
              }
              const nextCheckedIn = item.id != null ? checkedInById.get(item.id) : undefined;
              const nextCheckout = item.id != null ? checkoutById.get(item.id) : undefined;
              if (nextCheckedIn !== undefined || nextCheckout !== undefined) {
                next = {
                  ...next,
                  ...(nextCheckedIn !== undefined ? { checked_in: Boolean(nextCheckedIn) } : {}),
                  ...(nextCheckout !== undefined ? { checkout: Boolean(nextCheckout) } : {}),
                };
              }
              if (item.id != null && editIdSet.has(Number(item.id))) {
                next = {
                  ...next,
                  adults: Math.max(0, Number(adults) || 0),
                  children: Math.max(0, Number(children) || 0),
                };
              }
              return next;
            });
            await persistRoomDetailsChanges(nextItems, { successMessage: 'Booking items updated.' });
          }}
        />

        <UpgradeRoomDrawer
          open={upgradeDrawerOpen}
          onClose={() => { setUpgradeDrawerOpen(false); setUpgradeDrawerGroup(null); }}
          onUpgradeSuccess={async (payload) => {
            try {
              const { itemIds, roomTypeId, ratePlanId, allocatedRoomId, amountPerNight, upgradeFromDate, adults, children } = payload;
              const changedAtIso = moment().toISOString();
              const roomTypeName = roomTypes.find((rt) => rt.id === roomTypeId)?.name || '';
              const fromDateStr = upgradeFromDate
                ? (typeof upgradeFromDate === 'string' ? upgradeFromDate : moment(upgradeFromDate).format('YYYY-MM-DD'))
                : null;
              const pricePerNight =
                typeof amountPerNight === 'number' && amountPerNight >= 0 ? amountPerNight : null;
              const adultsValue = Number.isFinite(Number(adults)) ? Math.max(0, Number(adults)) : null;
              const childrenValue = Number.isFinite(Number(children)) ? Math.max(0, Number(children)) : null;
              const bookingStatus = String(booking?.status || '').toLowerCase();
              const checkInDateStr = booking?.check_in_date
                ? (moment.isMoment(booking.check_in_date)
                    ? booking.check_in_date.format('YYYY-MM-DD')
                    : moment(booking.check_in_date).format('YYYY-MM-DD'))
                : null;
              const todayStr = moment().format('YYYY-MM-DD');
              const isCheckedInFlow = ['checked_in', 'partial_checkin', 'partial_checkout'].includes(bookingStatus);
              const applyToWholeSequence =
                !isCheckedInFlow ||
                !fromDateStr ||
                (checkInDateStr ? fromDateStr <= checkInDateStr : false);
              const targetTypeId = Number(roomTypeId || 0) || null;
              const targetItems = bookingItems.filter((it) => itemIds.includes(it.id));
              const historicalTypeIdsBeforeFromDate = new Set();
              if (fromDateStr) {
                targetItems.forEach((it) => {
                  const itTypeId = Number(it?.room_type_id || it?.room_type?.id || 0) || null;
                  const itDateStr = it?.date
                    ? (moment.isMoment(it.date) ? it.date.format('YYYY-MM-DD') : String(it.date).slice(0, 10))
                    : '';
                  if (itTypeId && itDateStr && itDateStr < fromDateStr) {
                    historicalTypeIdsBeforeFromDate.add(itTypeId);
                  }
                });
              }
              const hasAnyRoomTypeChange = targetItems.some((it) => {
                const oldTypeId = Number(it.room_type_id || it.room_type?.id || 0) || null;
                return oldTypeId && targetTypeId && oldTypeId !== targetTypeId;
              });
              const isAllocatedRoomChanging =
                allocatedRoomId != null &&
                targetItems.some((it) => Number(it.allocated_room_id || 0) !== Number(allocatedRoomId));
              const shouldCheckoutPastDatesInSequence =
                hasAnyRoomTypeChange || isAllocatedRoomChanging;

              const changedRoomTypeItemIds = new Set();
              const nextItems = bookingItems.map((item) => {
                if (!itemIds.includes(item.id)) return item;
                const oldRoomTypeId = Number(item.room_type_id || item.room_type?.id || 0) || null;
                const itemDateStr = item.date
                  ? (moment.isMoment(item.date) ? item.date.format('YYYY-MM-DD') : String(item.date).slice(0, 10))
                  : '';
                const applyUpgrade = applyToWholeSequence
                  || !fromDateStr
                  || (itemDateStr && itemDateStr >= fromDateStr)
                  || (!streamHasOpenNightOnOrAfter(targetItems, fromDateStr) && item?.checkout !== true);
                if (applyUpgrade) {
                  const hasRoomTypeChanged = oldRoomTypeId && Number(oldRoomTypeId) !== Number(roomTypeId);
                  if (hasRoomTypeChanged && item.id != null) {
                    changedRoomTypeItemIds.add(item.id);
                  }
                  const shouldMarkUpgradeLineage = !applyToWholeSequence && hasRoomTypeChanged;
                  const existingUpgradeFromTypeId =
                    item.upgraded_from_room_type_id ??
                    item.upgraded_from_room_type?.id ??
                    null;
                  let nextUpgradeFromTypeId = existingUpgradeFromTypeId;
                  if (hasRoomTypeChanged) {
                    if (shouldMarkUpgradeLineage) {
                      const oldTypeNum = Number(oldRoomTypeId || 0) || null;
                      const existingSourceNum = Number(existingUpgradeFromTypeId || 0) || null;
                      const hasOldTypeHistory = oldTypeNum
                        ? historicalTypeIdsBeforeFromDate.has(oldTypeNum)
                        : false;
                      // Preserve chain in repeated same-day upgrades (A->B then B->C),
                      // where B may have no historical row yet.
                      nextUpgradeFromTypeId =
                        !hasOldTypeHistory && existingSourceNum
                          ? existingSourceNum
                          : oldTypeNum;
                      if (nextUpgradeFromTypeId && Number(nextUpgradeFromTypeId) === Number(roomTypeId)) {
                        nextUpgradeFromTypeId = null;
                      }
                    } else {
                      nextUpgradeFromTypeId = null;
                    }
                  }
                  return {
                    ...item,
                    room_type_id: roomTypeId,
                    room_type_name: roomTypeName,
                    rate_plan_id: ratePlanId,
                    allocated_room_id: allocatedRoomId,
                    room_sequence: item.room_sequence ?? 1,
                    checkout: false,
                    upgraded_from_room_type_id: nextUpgradeFromTypeId,
                    segment_started_at: changedAtIso,
                    room_changed_at: changedAtIso,
                    ...(adultsValue != null && { adults: adultsValue }),
                    ...(childrenValue != null && { children: childrenValue }),
                    ...(pricePerNight != null && { price: pricePerNight }),
                  };
                }
                // Mid-stay upgrade: only mark past dates as checkout when room or room type actually changed,
                // so we release the old room state. If only amount/price changed (same room, same type),
                // do not checkout previous days in the sequence.
                if (
                  shouldCheckoutPastDatesInSequence &&
                  !applyToWholeSequence &&
                  shouldCheckoutOldRoomSegment(item, fromDateStr, targetItems) &&
                  itemDateStr &&
                  itemDateStr < todayStr
                ) {
                  return { ...item, checkout: true, segment_ended_at: changedAtIso };
                }
                return item;
              });
              if (allocatedRoomId) {
                const selectedRoomIdNum = Number(allocatedRoomId);
                const seenDates = new Set();
                const duplicateDates = new Set();
                nextItems.forEach((item) => {
                  const roomIdNum = Number(item.allocated_room_id || 0);
                  if (roomIdNum !== selectedRoomIdNum || item.checkout === true) return;
                  const itemDateStr = item.date
                    ? (moment.isMoment(item.date) ? item.date.format('YYYY-MM-DD') : String(item.date).slice(0, 10))
                    : '';
                  if (!itemDateStr) return;
                  if (seenDates.has(itemDateStr)) {
                    duplicateDates.add(itemDateStr);
                  } else {
                    seenDates.add(itemDateStr);
                  }
                });
                if (duplicateDates.size > 0) {
                  setError(
                    `Same room cannot be allocated to multiple active rows on ${[...duplicateDates].join(', ')}. Please select separate rooms.`
                  );
                  setErrorDialogOpen(true);
                  return;
                }
              }
              setBookingItems(nextItems);
              const updateData = {
                booking_items: nextItems.map((item) => ({
                  id: item.id,
                  date: item.date,
                  price: parseFloat(item.price) || 0,
                  rate_plan_id: item.rate_plan_id || null,
                  allocated_room_id: item.allocated_room_id || null,
                  room_type_id: item.room_type_id || null,
                  room_sequence:
                    item.id != null && changedRoomTypeItemIds.has(item.id)
                      ? null
                      : (item.room_sequence ?? 1),
                  checked_in: item.checked_in ?? false,
                  checkout: item.checkout ?? false,
                  upgraded_from_room_type_id: item.upgraded_from_room_type_id ?? null,
                  adults: item.adults,
                  children: item.children,
                  ...(item.segment_started_at ? { segment_started_at: item.segment_started_at } : {}),
                  ...(item.segment_ended_at ? { segment_ended_at: item.segment_ended_at } : {}),
                  ...(item.room_changed_at ? { room_changed_at: item.room_changed_at } : {}),
                })),
              };
              await updateBooking(bookingId, updateData);
              setSuccessMessage('Room upgraded successfully.');
              await fetchBookingData();
            } catch (err) {
              const errMsg = getErrorMessage(err, 'Failed to upgrade room.');
              setError(errMsg);
              setErrorDialogOpen(true);
            }
          }}
          bookingId={normalizedBookingId}
          group={upgradeDrawerGroup}
          booking={booking}
          roomTypes={roomTypes}
          ratePlans={ratePlans}
          getAvailableRoomTypes={getAvailableRoomTypes}
        />

        {/* Payment Receipt Camera Modal */}
        {paymentCameraOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              zIndex: 1500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                stopPaymentCamera();
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                Capture Payment Receipt
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'black',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <video
                  ref={paymentReceiptVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <canvas ref={paymentReceiptCanvasRef} style={{ display: 'none' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, width: '100%' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capturePaymentReceipt}
                  size="large"
                  sx={{ minWidth: 150, py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                >
                  Capture Photo
                </Button>
                <Button
                  variant="outlined"
                  onClick={stopPaymentCamera}
                  size="large"
                  sx={{
                    minWidth: 150,
                    py: 1.5,
                    fontSize: '1rem',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Save Section Confirmation Dialog */}
        <Dialog
          open={removeRoomConfirmDialog.open}
          onClose={() => {
            if (savingSection === 'rooms') return;
            setRemoveRoomConfirmDialog({ open: false, group: null, totalDisplayRows: 0, targetItemIds: [] });
            setRemoveRoomConfirmText('');
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 620 },
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning sx={{ color: isDark ? '#ff8a80' : '#d32f2f', fontSize: 24 }} />
              <Box>
                <Typography variant="h6" sx={{ color: isDark ? surface.text : '#333', fontWeight: 600, lineHeight: 1.2 }}>
                  Remove Room
                </Typography>
                <Typography sx={{ color: isDark ? surface.subtleText : '#666', fontSize: '0.78rem', mt: 0.2 }}>
                  Confirm permanent room sequence removal
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: isDark ? surface.text : '#444', lineHeight: 1.55 }}>
              This will remove this room from <strong>Room Details</strong> for all its dates.
            </Typography>
            {(removeRoomConfirmDialog.totalDisplayRows || 0) <= 1 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                At least one room must remain in the booking.
              </Alert>
            )}
            {removeRoomConfirmDialog?.group?.room_type_display_name && (
              <Typography sx={{ mt: 1, color: isDark ? surface.subtleText : '#666', fontSize: '0.9rem' }}>
                Room: {removeRoomConfirmDialog.group.room_type_display_name}
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label="Type delete to confirm"
              value={removeRoomConfirmText}
              onChange={(e) => setRemoveRoomConfirmText(e.target.value)}
              placeholder="delete"
              disabled={savingSection === 'rooms'}
              helperText="Enter exactly: delete"
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(EDIT_BOOKING_ACCENT, 0.35),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(EDIT_BOOKING_ACCENT, 0.55),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(EDIT_BOOKING_ACCENT, 0.9),
                  },
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRemoveRoomConfirmDialog({ open: false, group: null, totalDisplayRows: 0, targetItemIds: [] });
                setRemoveRoomConfirmText('');
              }}
              disabled={savingSection === 'rooms'}
              sx={{ color: isDark ? surface.subtleText : '#666' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRemoveRoomGroup}
              disabled={
                savingSection === 'rooms' ||
                (removeRoomConfirmDialog.totalDisplayRows || 0) <= 1 ||
                String(removeRoomConfirmText || '').trim().toLowerCase() !== 'delete'
              }
              startIcon={savingSection === 'rooms' ? <CircularProgress size={14} color="inherit" /> : <Delete />}
              sx={{
                fontWeight: 700,
                px: 2,
                borderRadius: 2,
                boxShadow: isDark ? '0 10px 24px rgba(244,67,54,0.36)' : '0 10px 22px rgba(244,67,54,0.26)',
              }}
            >
              {savingSection === 'rooms' ? 'Removing...' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={saveConfirmDialog.open}
          onClose={() => !savingSection && setSaveConfirmDialog({ open: false, section: null })}
          PaperProps={{
            sx: {
              backgroundColor: surface.surface,
              border: `1px solid ${surface.surfaceBorder}`,
            },
          }}
        >
          <DialogTitle sx={{ color: isDark ? '#fff' : '#333' }}>
            Save {getSectionTitle(saveConfirmDialog.section) || 'changes'}?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: isDark ? surface.text : '#333' }}>
              Are you sure you want to save changes to {getSectionTitle(saveConfirmDialog.section) || 'this section'}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setSaveConfirmDialog({ open: false, section: null })}
              disabled={savingSection}
              sx={{ color: isDark ? surface.subtleText : '#666' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSection}
              disabled={savingSection}
              sx={{
                backgroundColor: EDIT_BOOKING_ACCENT,
                color: '#000',
                '&:hover': { backgroundColor: EDIT_BOOKING_ACCENT, opacity: 0.95 },
              }}
            >
              {savingSection ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Payment Confirmation Dialog */}
        <Dialog
          open={deleteConfirmDialog.open}
          onClose={() => !deletingPayment && setDeleteConfirmDialog({ open: false, payment: null })}
          PaperProps={{
            sx: {
              backgroundColor: surface.surface,
              border: `1px solid ${surface.surfaceBorder}`,
            },
          }}
        >
          <DialogTitle sx={{ color: isDark ? '#fff' : '#333' }}>
            Delete Payment
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: isDark ? surface.text : '#333' }}>
              Are you sure you want to delete this payment of{' '}
              <strong>₹{(deleteConfirmDialog.payment?.amount || 0).toLocaleString()}</strong>
              {deleteConfirmDialog.payment?.payment_method && (
                <> ({deleteConfirmDialog.payment.payment_method.replace(/_/g, ' ')})</>
              )}
              ? This action cannot be undone.
            </Typography>
            {deletePaymentBlockedByAdvance && (
              <Typography sx={{ color: '#f44336', mt: 2, fontWeight: 500 }}>
                Total payments cannot be less than the advance amount (₹{(deletePaymentBlockedByAdvance.advanceAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}).
                Deleting this payment would make total payments too low. This payment cannot be deleted.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteConfirmDialog({ open: false, payment: null })}
              disabled={deletingPayment}
              sx={{ color: isDark ? surface.subtleText : '#666' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeletePayment}
              disabled={deletingPayment || !!deletePaymentBlockedByAdvance}
              sx={{
                backgroundColor: '#f44336',
                color: '#fff',
                '&:hover': { backgroundColor: deletePaymentBlockedByAdvance ? undefined : '#d32f2f' },
              }}
            >
              {deletingPayment ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error Dialog - popup for save/action errors with clear message */}
        <Dialog
          open={errorDialogOpen}
          onClose={closeErrorDialog}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 400,
              maxWidth: 560,
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#f44336', fontSize: 28 }} />
            <Typography variant="h6" component="span">
              Unable to Save
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
              }}
            >
              {error || 'An unexpected error occurred.'}
            </Typography>
            {error && (error.includes('Insufficient inventory') || error.includes('blocked/disabled')) && (
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                The room type may be blocked or disabled in Daily Inventory for the selected dates. 
                Check the Inventory calendar to enable availability, or choose a different room type/date.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={closeErrorDialog}
              variant="contained"
              sx={{ backgroundColor: EDIT_BOOKING_ACCENT, color: '#000' }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSuccessMessage(null)} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!cameraPermissionMessage}
          autoHideDuration={9000}
          onClose={() => setCameraPermissionMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setCameraPermissionMessage(null)}
            severity="warning"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {cameraPermissionMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default EditBookingPage;
